<template>
  <v-card class="fill-height" dark>
    <PersonalRecordAnimation :visible.sync="showPRAnimation" :record-type="prAnimationData.recordType" :new-value="prAnimationData.newValue" />

    <v-card-text class="exercise-list pa-0" :style="{ 'margin-top': initialHeight + 10 + 'px' }">
      <v-list dense :style="getSelectedExercise ? 'overflow-x: hidden;' : ''" class="pa-0 justify-content-center">
        <template v-if="warmupOnly.length">
          <v-list-item class="section-header warmup-marker mt-0 px-0 justify-content-center">
            <div class="section-header-content warmup">
              <v-avatar size="32" class="mr-3">
                <v-img src="/img/workout/calories.png" />
              </v-avatar>
              <div class="section-title">Aufwärmen</div>
            </div>
          </v-list-item>
          <v-list-item
            v-for="exercise in warmupOnly"
            :key="exercise._id + '-warmup'"
            class="exercise-card exercise-card--warmup mx-auto px-1"
            :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
            @click="open(exercise, $event)"
            :ripple="false">
            <v-card ripple flat class="card-inner" :style="{ border: isExerciseCompleted(exercise) ? '2px solid green' : '2px solid orange' }">
              <v-icon v-if="isExerciseCompleted(exercise)" :color="activeSplit.isHyrox ? '#ffed00' : 'green'" small class="completed-checkmark">
                mdi-check-circle
              </v-icon>
              <v-row no-gutters align="center">
                <v-col cols="3" class="d-flex justify-center">
                  <v-img :src="getImageForExercise(exercise)" height="64px" width="64px" class="card-img rounded" />
                </v-col>
                <v-col cols="9" class="pl-2">
                  <div class="title font-weight-bold mb-1">{{ exercise.title }}</div>
                  <div class="subtitle-2">
                    <template v-if="exercise.title === 'Senso Pro'">
                      <span v-if="isExerciseCompleted(exercise)" class="green--text text--lighten-1">Durchgang abgeschlossen</span>
                      <span v-else>Bereit für den Durchgang</span>
                    </template>
                    <template v-else>
                      <span style="position: absolute; bottom: 5px; right: 10px"> {{ exercise.completedSetCount }} / 1 </span>
                      <v-progress-linear :value="exercise.completedSetCount * 100" color="orange" height="4" rounded class="mt-2"></v-progress-linear>
                    </template>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-list-item>
        </template>

        <template v-if="regularExercises.length">
          <v-list-item v-if="warmupOnly.length" class="section-header training-marker justify-content-center">
            <div class="section-header-content training">
              <v-avatar size="32" class="mr-3">
                <v-img src="/img/workout/kettlebell.png" />
              </v-avatar>
              <div class="section-title">Training</div>
            </div>
          </v-list-item>
          <template v-for="(exercise, index) in regularExercises">
            <v-list-item
              v-if="!exercise.isSuperset"
              :key="exercise._id + index + '-regular'"
              class="exercise-card px-1 mx-auto"
              :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
              @click="open(exercise, $event)"
              :ripple="false">
              <v-card
                ripple
                flat
                class="card-inner rounded-xl"
                :class="{
                  'exercise-card-inner--completed-hyrox': activeSplit.isHyrox && isExerciseCompleted(exercise),
                  'exercise-card-inner--completed': !activeSplit.isHyrox && isExerciseCompleted(exercise),
                  'intensity-glow': exercise.method,
                }"
                :style="getCardStyle(exercise)">
                <v-icon v-if="isExerciseCompleted(exercise)" color="green" small class="completed-checkmark">mdi-check-circle</v-icon>
                <v-row no-gutters align="center">
                  <v-col cols="3" class="d-flex justify-center">
                    <v-img :src="getImageForExercise(exercise)" height="64px" width="64px" class="card-img rounded" />
                  </v-col>
                  <v-col cols="9" class="pl-2">
                    <div class="title font-weight-bold mb-1">
                      {{ exercise.title }}
                      <v-chip v-if="exercise.warmup" color="orange" x-small outlined class="intensity-chip ml-1" dark label>
                        <v-icon x-small>mdi-fire</v-icon>
                      </v-chip>
                      <v-chip
                        v-if="exercise.method && intensityTechniqueMapping[exercise.method]"
                        :color="intensityTechniqueMapping[exercise.method].color"
                        x-small
                        outlined
                        class="ml-1 intensity-chip"
                        dark
                        label>
                        <v-icon x-small>{{ intensityTechniqueMapping[exercise.method].icon }}</v-icon>
                      </v-chip>
                    </div>

                    <div class="subtitle-2">
                      <template v-if="exercise.title === 'Senso Pro'">
                        <span v-if="isExerciseCompleted(exercise)" class="green--text text--lighten-1">Durchgang abgeschlossen</span>
                        <span v-else>Bereit für den Durchgang</span>
                      </template>
                      <template v-else>
                        <span style="position: absolute; bottom: 5px; right: 10px"
                          >{{ exercise.completedSetCount }} / {{ exercise.plannedSetCount }}
                        </span>
                        <v-progress-linear
                          v-if="exercise.plannedSetCount > 0"
                          :value="(exercise.completedSetCount / exercise.plannedSetCount) * 100"
                          height="4"
                          :color="activeSplit.isHyrox ? '#ffed00' : isExerciseCompleted(exercise) ? 'green' : 'primary'"
                          rounded
                          class="mt-2"></v-progress-linear
                      ></template>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-list-item>

            <v-list-item
              v-else
              :key="exercise.supersetId + '-superset'"
              class="exercise-card px-1 mx-auto"
              :class="{ 'exercise-line-completed': isExerciseCompleted(exercise) }"
              @click="open(exercise, $event)"
              :ripple="false">
              <v-card
                ripple
                flat
                class="card-inner rounded-xl superset-card-inner"
                :class="{ 'exercise-card-inner--completed': isExerciseCompleted(exercise) }">
                <v-icon v-if="isExerciseCompleted(exercise)" color="green" small class="completed-checkmark">mdi-check-circle</v-icon>
                <div class="superset-label">
                  <v-icon small left>mdi-arrow-collapse-all</v-icon>
                  Supersatz
                </div>
                <v-row no-gutters align="center">
                  <v-col cols="3" class="d-flex flex-column align-center justify-center">
                    <v-avatar size="40" class="mb-1 elevation-2">
                      <v-img :src="getImageForExercise(exercise.exercises[0])" />
                    </v-avatar>
                    <v-avatar size="40" class="mt-1 elevation-2">
                      <v-img :src="getImageForExercise(exercise.exercises[1])" />
                    </v-avatar>
                  </v-col>
                  <v-col cols="9" class="pl-3">
                    <div v-for="subEx in exercise.exercises" :key="subEx._id" class="mb-1">
                      <div class="title font-weight-bold" style="line-height: 1.2">{{ subEx.title }}</div>
                      <div class="subtitle-2">{{ getCompletedSetsForSubExercise(exercise, subEx).length }} / {{ subEx.sets.length }} Sätze</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-list-item>
          </template>
          <v-list-item class="section-header my-0 pb-7 justify-content-center" @click="promptFinishWorkout">
            <div class="section-header-content finish">
              <div class="section-title">Training abschliessen</div>
            </div>
          </v-list-item>
        </template>
      </v-list>
    </v-card-text>
    <v-dialog v-model="showWeightPropagationDialog" max-width="450px" dark persistent content-class="elevation-0" overlay-opacity="0.8">
      <v-card v-if="propagationData" class="rounded-xl pa-3 glass-card-dialog propagation-dialog-card">
        <v-card-title class="font-weight-bold justify-center pb-1">
          <v-icon left :color="propagationData.isIncrease ? 'green accent-3' : 'orange'">
            {{ propagationData.isIncrease ? "mdi-trending-up" : "mdi-trending-down" }}
          </v-icon>
          Plan-Update Vorschlag
        </v-card-title>

        <v-card-text class="white--text mt-4">
          <template v-if="propagationData.type === 'absolute'">
            <v-row align="center" justify="center" no-gutters class="text-center">
              <v-col>
                <div class="text-caption grey--text">Bisheriger Plan</div>
                <div class="weight-box old-weight">{{ propagationData.oldValue }}</div>
              </v-col>
              <v-col cols="auto" class="px-2">
                <v-icon size="32" class="arrow-anim" color="grey lighten-1">mdi-arrow-right-bold</v-icon>
              </v-col>
              <v-col>
                <div class="text-caption" :class="propagationData.isIncrease ? 'green--text text--lighten-2' : 'orange--text text--lighten-2'">
                  Dein neuer Plan
                </div>
                <div class="weight-box new-weight" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                  {{ propagationData.newValue }}
                </div>
              </v-col>
            </v-row>
            <p class="text-center mt-6 body-1">{{ propagationData.description }}</p>
          </template>

          <template v-else>
            <div class="text-center">
              <div class="text-caption grey--text">Bisheriger Plan (nächste Sätze)</div>
              <div class="weight-box old-weight mt-1">
                <span v-for="(val, i) in propagationData.oldValue" :key="`old-${i}`" class="mx-2">{{ val }} kg</span>
              </div>

              <div class="connector-wrapper">
                <div class="operation-indicator" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                  <v-icon>{{ propagationData.isIncrease ? "mdi-plus" : "mdi-minus" }}</v-icon>
                  <span>{{ propagationData.newValue.replace("+", "") }} kg</span>
                </div>
              </div>

              <div class="text-caption" :class="propagationData.isIncrease ? 'green--text text--lighten-2' : 'orange--text text--lighten-2'">
                Dein neuer Plan
              </div>
              <div class="weight-box new-weight mt-1" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                <span
                  v-for="(val, i) in propagationData.newValueCalculated"
                  :key="`new-${i}`"
                  class="new-weight-value mx-2"
                  :style="{ 'animation-delay': `${i * 100}ms` }">
                  {{ val }} kg
                </span>
              </div>

              <p class="text-center mt-5 body-2 grey--text text--lighten-1">{{ propagationData.subDescription }}</p>
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0 d-block text-center">
          <v-btn :color="propagationData.isIncrease ? 'green' : 'orange'" depressed rounded large block class="mb-2" @click="executePropagation">
            <v-icon left>mdi-check-all</v-icon>
            Alle Sätze anpassen
          </v-btn>
          <v-btn text small color="grey" @click="closePropagationDialog"> Nein, nur diesen Satz </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showIncompleteDialog" max-width="450px" dark persistent content-class="elevation-0" overlay-opacity="0.8">
      <v-card class="incomplete-dialog-card text-center pa-5">
        <div class="incomplete-dialog-bg"></div>
        <v-icon size="100" color="orange darken-1" class="alert-icon mb-4">mdi-alert-decagram-outline</v-icon>
        <h2 class="font-weight-bold text-h4 white--text mb-3">Training unvollständig</h2>
        <p class="white--text text--lighten-1 mb-6">Du hast noch nicht alle geplanten Sätze abgeschlossen. Folgende Übungen sind noch offen:</p>

        <v-list dense color="transparent" class="py-0 mb-6 text-left incomplete-list">
          <v-list-item v-for="ex in incompleteExercises" :key="ex.title" class="px-1 mb-2">
            <v-list-item-icon class="mr-4 my-auto">
              <v-icon color="grey lighten-1">mdi-weight-lifter</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold white--text">{{ ex.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ ex.completed }} von {{ ex.planned }} Sätzen erledigt</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-row dense>
          <v-col cols="12">
            <v-btn large rounded block color="orange darken-2" class="font-weight-bold" @click="showIncompleteDialog = false"> Fortsetzen </v-btn>
          </v-col>
          <v-col cols="12" class="pt-2">
            <v-btn large rounded block text @click="finishAnyway" class="font-weight-bold grey--text">Trotzdem beenden</v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <transition name="grow">
      <div v-if="getSelectedExercise" class="overlay">
        <div class="main-card-container" :class="{ 'is-dragging-over': dragState.active }">
          <v-card class="dialog-card fullscreen px-4" dark>
            <v-carousel v-if="!getSelectedExercise.isSuperset" height="220px" hide-delimiters :show-arrows="selectedExerciseImageCarousel.length > 1">
              <v-carousel-item class="text-center" v-for="(item, i) in selectedExerciseImageCarousel" :key="i">
                <v-img contain :src="item.src" height="200px">
                  <template #placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center"><v-progress-circular indeterminate /></v-row>
                  </template>
                </v-img>
                <span class="caption-text">
                  {{ selectedExerciseImageCarousel.length > 1 ? "Schritt " + (i + 1) + ": " : "" }} {{ item.description }}</span
                >
              </v-carousel-item>
            </v-carousel>
            <div v-else class="superset-image-container d-flex justify-center align-center" style="height: 220px">
              <div
                v-for="subEx in getSelectedExercise.exercises"
                :key="subEx.instanceId"
                class="superset-avatar-wrapper mx-2"
                :class="{ 'active-superset-exercise': getActiveSupersetSubExercise && getActiveSupersetSubExercise.instanceId === subEx.instanceId }"
                @click="showImage(subEx)"
                style="cursor: pointer">
                <div>
                  <v-avatar size="100" class="elevation-4">
                    <v-img :src="getImageForExercise(subEx)" />
                  </v-avatar>
                  <div class="caption text-center mt-2 white--text text-truncate" style="max-width: 100px">
                    {{ subEx.title }}
                  </div>
                </div>
              </div>
            </div>
            <v-card-text ref="dialogContent" class="dialog-content mt-3 px-0">
              <v-alert
                style="background: rgba(255, 255, 255, 0.05)"
                v-if="selectedExerciseMethodDetail"
                :color="selectedExerciseMethodDetail.color"
                border="left"
                elevation="2"
                colored-border
                class="mb-4 intensity-alert"
                dark>
                <div class="d-flex align-center">
                  <v-icon class="mr-3">{{ selectedExerciseMethodDetail.icon }}</v-icon>
                  <div class="intensity-alert-title">{{ selectedExerciseMethodDetail.descriptionShort }}</div>
                  <v-btn icon small class="ml-auto" @click="toggleIntensityAlertExpansion"
                    ><v-icon>{{ isIntensityAlertExpanded ? "mdi-chevron-up" : "mdi-information-outline" }}</v-icon></v-btn
                  >
                </div>
                <v-expand-transition>
                  <div v-show="isIntensityAlertExpanded" class="intensity-details-expanded mt-2">
                    <v-divider class="my-3"></v-divider>
                    <div class="px-1 content-wrapper">
                      <p class="subtitle-1 font-weight-bold">{{ selectedExerciseMethodDetail.details.summary }}</p>
                      <p class="body-2 mb-4">{{ selectedExerciseMethodDetail.description }}</p>
                      <div v-if="selectedExerciseMethodDetail.details.advantages && selectedExerciseMethodDetail.details.advantages.length">
                        <h4
                          class="mt-4 mb-1 text-uppercase text-caption"
                          :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color) }">
                          Vorteile
                        </h4>
                        <v-list dense color="transparent" class="pa-0">
                          <v-list-item
                            v-for="(advantage, index) in selectedExerciseMethodDetail.details.advantages"
                            :key="'adv-' + index"
                            class="px-0 py-1">
                            <v-list-item-icon class="mr-2 my-0"
                              ><v-icon small :color="getIconColorForItem(advantage, 'advantage')">{{ advantage.icon }}</v-icon></v-list-item-icon
                            >
                            <v-list-item-content class="py-0"
                              ><v-list-item-title class="body-2 white--text">{{ advantage.text }}</v-list-item-title></v-list-item-content
                            >
                          </v-list-item>
                        </v-list>
                      </div>
                      <div v-if="selectedExerciseMethodDetail.details.whyDoIt && selectedExerciseMethodDetail.details.whyDoIt.length">
                        <h4
                          class="mt-4 mb-1 text-uppercase text-caption"
                          :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color, true) }">
                          Warum diese Technik?
                        </h4>
                        <v-list dense color="transparent" class="pa-0">
                          <v-list-item
                            v-for="(reason, index) in selectedExerciseMethodDetail.details.whyDoIt"
                            :key="'reason-' + index"
                            class="px-0 py-1">
                            <v-list-item-icon class="mr-2 my-0"
                              ><v-icon small :color="getIconColorForItem(reason, 'reason')">{{ reason.icon }}</v-icon></v-list-item-icon
                            >
                            <v-list-item-content class="py-0"
                              ><v-list-item-title class="body-2 white--text">{{ reason.text }}</v-list-item-title></v-list-item-content
                            >
                          </v-list-item>
                        </v-list>
                      </div>
                    </div>
                    <v-divider class="mt-4 mb-2"></v-divider>
                    <div class="d-flex justify-end pa-1">
                      <v-btn text small :color="selectedExerciseMethodDetail.color" @click="toggleIntensityAlertExpansion"
                        ><v-icon left small>mdi-chevron-up</v-icon>Weniger anzeigen</v-btn
                      >
                    </div>
                  </div>
                </v-expand-transition>
              </v-alert>
              <v-card
                v-if="activeSplit && !activeSplit.isHyrox"
                flat
                class="mb-4 pa-4 equipment-card"
                :class="{ 'orange--border': hasWarmup && slideIndex === 0 }">
                <template
                  v-if="
                    getSelectedExercise &&
                    getSelectedExercise.general_exercise_settings &&
                    getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                  ">
                  <div class="preset-label">Geräteeinstellungen</div>
                  <div class="plan-settings">
                    <v-chip
                      v-for="(setting, i) in getSelectedExercise.general_exercise_settings.filter((s) => s.value)"
                      :key="'device-' + i"
                      outlined
                      small
                      class="preset-chip mr-2 mb-2">
                      {{ setting.text }}: {{ setting.value }}
                    </v-chip>
                    <span
                      v-if="
                        !getSelectedExercise.general_exercise_settings || !getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                      "
                      class="grey--text text--lighten-1 text-caption">
                      Keine spezifischen Geräteeinstellungen hinterlegt.
                    </span>
                  </div>
                </template>
                <template>
                  <div class="d-flex justify-space-between align-center mb-2">
                    <div class="preset-label">{{ slideLabel }}</div>
                    <div v-if="!getSelectedExercise.isSuperset && (hasWarmup || (displayPlanSettings && displayPlanSettings.length > 0))">
                      <v-btn icon @click="prevSlide" :disabled="slideIndex === 0"><v-icon>mdi-chevron-left</v-icon></v-btn>
                      <v-btn icon @click="nextSlide" :disabled="slideIndex >= totalSlides - 1"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    </div>
                  </div>
                  <div class="plan-settings">
                    <template v-if="hasWarmup && slideIndex === 0">
                      <v-chip v-for="(w, i) in warmupSettings" :key="'w' + i" outlined small class="preset-chip orange mr-2 mb-2"
                        >{{ w.label }}: {{ w.value }}{{ w.unit || "" }}</v-chip
                      >
                    </template>
                    <template v-else-if="displayPlanSettings && displayPlanSettings.length > 0">
                      <v-chip v-for="(s, i) in displayPlanSettings" :key="'p' + i" outlined small class="preset-chip mr-2 mb-2"
                        >{{ s.label }}: {{ s.value }}</v-chip
                      >
                    </template>
                    <template v-else><span class="grey--text text--lighten-1 text-caption">Keine spezifischen Satzvorgaben.</span></template>
                  </div>
                </template>
              </v-card>
              <template v-if="isSensoProActive">
                <v-card flat class="mb-4 pa-4 finished-sets-card">
                  <v-card-title class="pa-0 pb-2">Notizen</v-card-title>
                  <div v-if="currentSensoProSet">
                    <div class="d-flex align-center" v-if="!editingNote">
                      <p class="mb-0 body-1">
                        <strong>Heute:</strong> {{ currentSensoProSet.find((s) => s.text === "note").value || "Keine Notiz" }}
                      </p>
                      <v-spacer></v-spacer>
                      <v-btn icon @click="editCurrentNote"><v-icon>mdi-pencil</v-icon></v-btn>
                    </div>
                    <div v-else>
                      <v-textarea v-model="editableNote" outlined rows="2" label="Heutige Notiz bearbeiten"></v-textarea>
                      <v-card-actions class="pa-0">
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancelEditNote">Abbrechen</v-btn>
                        <v-btn color="primary" @click="saveCurrentNote">Speichern</v-btn>
                      </v-card-actions>
                    </div>
                    <v-divider class="my-4"></v-divider>
                  </div>

                  <div v-if="historicalSensoProNotes.length > 0">
                    <div v-for="(item, i) in historicalSensoProNotes" :key="i" class="mb-2">
                      <div class="text-caption grey--text">{{ item.date }}</div>
                      <p class="body-2 mb-0">{{ item.note }}</p>
                    </div>
                  </div>
                  <div v-else class="text-center grey--text py-2 text-caption">Noch keine früheren Notizen vorhanden.</div>
                </v-card>
              </template>
              <template v-else>
                <v-card flat class="mb-4 pa-4 finished-sets-card">
                  <div class="history-nav d-flex justify-space-between align-center mb-4">
                    <v-btn icon @click="navigateHistory(1)" :disabled="historyViewIndex >= historicalSessions.length">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <div class="text-subtitle-1 font-weight-bold text-center">
                      <p
                        v-if="
                          historicalSessions[historyViewIndex - 1] &&
                          historicalSessions[historyViewIndex - 1].execution &&
                          historicalSessions[historyViewIndex - 1].execution.title.includes('HYROX')
                        ">
                        {{ getHyroxCategoryDetails(historicalSessions[historyViewIndex - 1].execution.title).text }}
                        <v-icon color="yellow">{{ getHyroxCategoryDetails(historicalSessions[historyViewIndex - 1].execution.title).icon }}</v-icon>
                      </p>
                      {{ historyTitle }}
                    </div>
                    <v-btn icon @click="navigateHistory(-1)" :disabled="historyViewIndex === 0">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>

                  <v-divider class="mb-2"></v-divider>

                  <transition name="table-container-transition" mode="out-in">
                    <div :key="historyViewIndex">
                      <v-simple-table class="completed-table" v-if="setsToDisplay.length > 0">
                        <thead>
                          <tr>
                            <th v-if="setsToDisplay && setsToDisplay.find((set) => set.subExerciseId)" class="text-center" style="width: 50px">
                              Übung
                            </th>
                            <th class="text-center" style="width: 40px">#</th>
                            <th>Details</th>
                            <th v-if="isFeedbackRelevant" class="text-center" style="width: 80px; white-space: nowrap">Feedback</th>
                          </tr>
                        </thead>
                        <transition-group tag="tbody" name="row">
                          <tr
                            v-for="(set, idx) in setsToDisplay"
                            :key="set.id || idx"
                            @click="isTodaySelected ? (set.skipped ? redoWarmup() : promptDeleteSet(idx, set)) : null"
                            :class="{
                              'clickable-row': isTodaySelected && !isWarmupBlocked,
                              'skipped-warmup-row': set.skipped,
                            }"
                            @click.middle="isTodaySelected && set.skipped ? redoWarmup() : null">
                            <td v-if="set.subExerciseId" class="text-center">
                              <v-avatar size="28" class="clickable-avatar" @click.stop="showImage(getSubExerciseForSet(set))">
                                <v-img :src="getImageForExercise(getSubExerciseForSet(set))" />
                              </v-avatar>
                            </td>
                            <td class="text-center">
                              <template v-if="hasWarmup && idx === 0">
                                <span v-if="set.skipped" class="grey--text text--lighten-1">--</span>
                                <span v-else>🔥</span>
                              </template>
                              <template v-else>{{ hasWarmup ? realSetNumber(idx) : idx + 1 }}</template>
                            </td>
                            <td :class="{ 'orange--text': hasWarmup && idx === 0 && !set.skipped, 'grey--text text--lighten-1': set.skipped }">
                              {{ set.skipped ? "Warmup übersprungen" : formatSetText(set) }}
                            </td>
                            <td v-if="isFeedbackRelevant" class="text-center" @click.stop>
                              <v-menu
                                v-if="!set.skipped && (getWeightDeviation(set, idx).deviated || hasRating(set) || isTodaySelected)"
                                v-model="set.showMenu"
                                offset-y
                                top
                                :close-on-content-click="false"
                                transition="scale-transition">
                                <template v-slot:activator="{ on, attrs }">
                                  <div v-bind="attrs" v-on="on" class="feedback-activator" @click="openFeedbackMenu(set, idx)">
                                    <span v-if="getFeedbackIcon(set, idx).isEmoji" class="feedback-emoji">
                                      {{ getFeedbackIcon(set, idx).icon }}
                                    </span>
                                    <v-icon v-else :color="getFeedbackIcon(set, idx).color" class="rating-icon">
                                      {{ getFeedbackIcon(set, idx).icon }}
                                    </v-icon>
                                  </div>
                                </template>

                                <v-card v-if="tooltipData" class="feedback-tooltip-card-v2 glass-card-dialog">
                                  <v-card-title class="pb-2">
                                    <span class="feedback-emoji mr-3" style="font-size: 1.5rem">{{ tooltipData.icon }}</span>
                                    <span class="title" :style="{ color: tooltipData.color }">{{ tooltipData.title }}</span>
                                    <v-spacer />
                                  </v-card-title>

                                  <v-divider class="mx-4"></v-divider>

                                  <v-card-text class="pt-4 pb-2 white--text">
                                    <div v-for="(item, itemIdx) in tooltipData.details" :key="item.label" class="d-flex justify-space-between mb-2">
                                      <span class="grey--text">{{ item.label }}</span>
                                      <span class="font-weight-bold" :class="item.color || 'white--text'">
                                        {{ item.value }}
                                      </span>
                                    </div>
                                  </v-card-text>

                                  <v-card-actions v-if="isTodaySelected && !hasRating(set)" class="d-flex justify-center pa-2">
                                    <v-btn text small color="primary" @click="openRetrospectiveFeedback(set, idx)">Jetzt bewerten</v-btn>
                                  </v-card-actions>
                                </v-card>
                              </v-menu>
                            </td>
                          </tr>
                        </transition-group>
                      </v-simple-table>
                      <div v-else class="no-sets text-center pa-4 grey--text">An diesem Tag wurden keine Sätze für diese Übung aufgezeichnet.</div>
                    </div>
                  </transition>
                </v-card>
              </template>

              <component
                v-if="isTodaySelected && exerciseForInput && !currentSensoProSet"
                :is="currentInputComponent"
                :is-hyrox="activeSplit.isHyrox"
                :exercise="exerciseForInput"
                :completed-sets="completedSets"
                :completed-sets-array="completedSetsArray"
                :is-competition="activeSplit.isCompetition"
                :warmup="hasWarmup"
                :plan-settings="getCurrentSatzVorgaben"
                @add-set="handleAddSet"
                @update-slide-index="slideIndex = $event"
                ref="inputComponentSection">
                <template #add-button
                  ><v-btn color="orange" class="ma-2">{{ addSetLabel }}</v-btn></template
                >
              </component>
            </v-card-text>
          </v-card>
        </div>
        <div
          v-if="previousExercise"
          class="swipe-handle left"
          @mousedown.prevent="dragStart('left', $event)"
          @touchstart.prevent="dragStart('left', $event)">
          <div class="swipe-indicator"></div>
        </div>

        <div
          v-if="nextExercise"
          class="swipe-handle right"
          @mousedown.prevent="dragStart('right', $event)"
          @touchstart.prevent="dragStart('right', $event)">
          <div class="swipe-indicator"></div>
        </div>

        <div
          v-if="dragState.active"
          class="drop-zone-card"
          :class="{
            'is-active': dragState.dropZoneActive,
            'is-left': dragState.origin === 'right',
            'is-right': dragState.origin === 'left',
          }"
          ref="dropZone">
          <v-img :src="getImageForExercise(dragState.avatarExercise)" class="drop-zone-image" cover>
            <div class="drop-zone-overlay">
              <v-icon class="drop-zone-icon">mdi-check-circle</v-icon>
              <div class="drop-zone-title">{{ dragState.avatarExercise.title }}</div>
            </div>
          </v-img>
        </div>

        <div v-if="dragState.active" class="drag-avatar" :class="{ 'on-target': dragState.dropZoneActive }" :style="dragAvatarStyle">
          <v-img :src="getImageForExercise(dragState.avatarExercise)" cover />
        </div>
      </div>
    </transition>

    <transition name="rest-timer-overlay-fade">
      <div v-if="showFeedbackOverlay" class="feedback-overlay d-flex justify-center align-center">
        <v-card dark class="feedback-card pa-4 text-center elevation-20 rounded-xl d-flex flex-column" width="95%" max-width="450px">
          <div class="feedback-card__header">
            <div style="position: absolute; top: 12px; right: 12px; z-index: 10">
              <v-btn icon small @click="showInfoView" class="mr-1" v-if="feedbackState !== 'info'"><v-icon>mdi-information-outline</v-icon></v-btn>
              <v-btn icon small @click="skipFeedback" v-if="feedbackState !== 'info'"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div class="mb-4"><v-img src="/img/roody_logo.png" height="50px" contain /></div>
          </div>

          <div class="feedback-card__content">
            <template v-if="isTimerActive">
              <div
                class="text-h5 font-weight-bold mb-1 status-title"
                :class="{ 'final-state': restTimerRemaining <= 0 }"
                :style="{ color: restTimerProgressColor, letterSpacing: '0.03em !important', textTransform: 'uppercase' }">
                {{ restTimerRemaining > 0 ? "Pause" : overtimeSeconds > 0 ? "Überzeit" : "Fertig!" }}
              </div>
              <div class="my-auto d-flex flex-column align-center">
                <v-progress-circular
                  :rotate="-90"
                  :size="$vuetify.breakpoint.xsOnly ? 170 : 180"
                  :width="$vuetify.breakpoint.xsOnly ? 12 : 14"
                  :value="restTimerProgress"
                  :color="restTimerProgressColor"
                  class="my-4"
                  :style="{ '--progress-color': restTimerProgressColor }">
                  <span
                    class="countdown-text"
                    :class="{ 'countdown-text-smaller': isCountdownTextTooLong, 'pulse-warning': restTimerRemaining > 0 && restTimerRemaining <= 5 }"
                    :style="{ color: restTimerProgressColor }">
                    {{
                      formatCountdownDisplay(
                        restTimerRemaining > 0 ? restTimerRemaining : overtimeSeconds,
                        restTimerRemaining <= 0 && overtimeSeconds > 0
                      )
                    }}
                  </span>
                </v-progress-circular>
                <div class="text-body-2 grey--text text--lighten-1">Geplant: {{ formatTimeForDisplay(plannedRestDuration) }}</div>
              </div>
            </template>

            <v-fade-transition mode="out-in">
              <div v-if="feedbackState === 'rating'" key="rating" class="feedback-section mt-1">
                <div v-if="weightDeviationStatus === 'higher'">
                  <h3 class="font-weight-bold mb-2 celebration-title">🎉 Super! Gewicht gesteigert!</h3>
                  <p class="body-1 mb-3">Wie hat sich das neue Gewicht angefühlt?</p>
                </div>
                <div v-else-if="weightDeviationStatus === 'lower'">
                  <h3 class="font-weight-bold mb-2" style="color: #ffcc80">Macht nichts, jeder Tag ist anders!</h3>
                  <p class="body-1 mb-3">Bewerte die Schwierigkeit mit dem angepassten Gewicht:</p>
                </div>
                <div v-else-if="feedbackState === 'timer_only'">
                  <v-card-title class="justify-center font-weight-bold pt-0 pb-1">Super Satz!</v-card-title>
                </div>

                <div v-else><v-card-title class="justify-center font-weight-bold pt-0 pb-1">Wie war der Satz?</v-card-title></div>
                <div class="rating-options-container">
                  <div class="rating-option rating-hard" :class="{ 'is-selected': selectedDifficulty === 'hard' }" @click="handleSmileyClick('hard')">
                    <div class="rating-emoji">🥵</div>
                    <div class="rating-text">
                      <div class="rating-label">Zu Schwer</div>
                      <div class="rating-desc">Wiederholungen kaum geschafft</div>
                    </div>
                  </div>
                  <div
                    class="rating-option rating-medium"
                    :class="{ 'is-selected': selectedDifficulty === 'medium' }"
                    @click="handleSmileyClick('medium')">
                    <div class="rating-emoji">🙂</div>
                    <div class="rating-text">
                      <div class="rating-label">Perfekt</div>
                      <div class="rating-desc">Fordernd, aber gute Form</div>
                    </div>
                  </div>
                  <div class="rating-option rating-easy" :class="{ 'is-selected': selectedDifficulty === 'easy' }" @click="handleSmileyClick('easy')">
                    <div class="rating-emoji">😎</div>
                    <div class="rating-text">
                      <div class="rating-label">Zu Leicht</div>
                      <div class="rating-desc">Deutlich mehr wäre möglich</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="feedbackState === 'show_weights'" key="weights" class="feedback-section mt-4">
                <p class="body-1 mb-3">Passe das Gewicht für dein <b>nächstes Training</b> an:</p>

                <v-text-field
                  v-model.number="adjustedWeight"
                  label="Neues Gewicht"
                  outlined
                  dense
                  dark
                  suffix="kg"
                  type="number"
                  inputmode="numeric"
                  class="mt-4 mx-2"
                  @focus="$event.target.select()"></v-text-field>
                <v-btn
                  small
                  text
                  color="grey"
                  @click="
                    feedbackState = 'rating'
                    adjustedWeight = null
                  "
                  class="mt-1">
                  Abbrechen
                </v-btn>
              </div>
              <div v-else-if="feedbackState === 'info'" key="info" class="feedback-section-info">
                <v-card-title class="justify-center font-weight-bold pt-0">Dein Feedback zählt!</v-card-title>
                <v-card-text class="white--text mt-2 text-left">
                  <p class="body-2">
                    Dein Feedback nach jedem Satz hilft uns oder deinem Trainer, deinen Trainingsplan kontinuierlich zu optimieren.
                  </p>
                  <v-divider class="my-3 gradient-divider"></v-divider>
                  <h4 class="font-weight-bold mb-2 text-subtitle-1">Was bedeuten die Bewertungen?</h4>
                  <v-list dense color="transparent" class="info-list">
                    <v-list-item class="mb-2 px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">🥵</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Zu Schwer</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Du hast die Wiederholungen kaum oder gar nicht geschafft. Das Gewicht war definitiv zu hoch.</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item class="mb-2 px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">🙂</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Perfekt</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Der Satz war fordernd, aber du hast alle Wiederholungen mit guter Form geschafft. Genau richtig!</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item class="px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">😎</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Zu Leicht</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Der Satz war einfach und du hättest ohne Probleme deutlich mehr Wiederholungen oder Gewicht bewältigen
                          können.</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </div>
            </v-fade-transition>
          </div>

          <div class="feedback-card__footer mt-4">
            <div v-if="feedbackState === 'info'" class="mt-auto">
              <v-btn block large rounded color="primary" @click="hideInfoView" class="elevation-4"><v-icon left>mdi-arrow-left</v-icon>Zurück</v-btn>
            </div>
            <div v-else class="mt-auto">
              <v-checkbox
                v-if="!isTimerActive && !disableFeedbackForExercise"
                v-model="doNotShowAgain"
                label="Für diese Übung nicht mehr fragen"
                dense
                class="mt-4"></v-checkbox>
              <div class="mt-1">
                <v-btn
                  block
                  x-large
                  rounded
                  :color="buttonColor"
                  @click="finishFeedbackAndCloseOverlay()"
                  class="elevation-4"
                  :disabled="!isFeedbackCompleted"
                  style="min-height: 56px; letter-spacing: 0.05em">
                  <v-icon left size="26">{{ buttonIcon }}</v-icon>
                  {{ buttonText }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </transition>

    <v-dialog v-model="exerciseDetailDialog" max-width="600px" dark>
      <v-card v-if="detailDialogExercise" class="glass-card-dialog">
        <v-toolbar flat color="transparent">
          <v-toolbar-title class="font-weight-bold">{{ detailDialogExercise.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="exerciseDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-carousel height="350px" hide-delimiters :show-arrows="detailCarouselItems.length > 1">
          <v-carousel-item class="text-center" v-for="(item, i) in detailCarouselItems" :key="i">
            <div>
              <v-img contain :src="item.src" height="280px">
                <template #placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate />
                  </v-row>
                </template>
              </v-img>
              <div class="caption-text grey--text text--lighten-1 pa-3">
                {{ item.description }}
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex"
import WeightRepInput from "./setInputTypes/weightRepInput.vue"
import SensoProInput from "./setInputTypes/sensoProInput.vue"
import TimeInput from "./setInputTypes/timeInput.vue"
import DistanceRepInput from "./setInputTypes/distanceRepInput.vue" // NEU
import HyroxInput from "./setInputTypes/hyroxInput.vue" // NEU

import { intensityTechniqueMapping } from "../utils/intensityTechniques.js"
import PersonalRecordAnimation from "./PersonalRecordAnimation.vue" // Pfad prüfen!

import moment from "moment"
import confetti from "canvas-confetti"

export default {
  name: "ExerciseList",
  components: { WeightRepInput, TimeInput, SensoProInput, DistanceRepInput, PersonalRecordAnimation, HyroxInput }, // HIER HINZUFÜGEN
  isSelectionDialogActive: false,
  selectionDialogMode: "swap", // 'swap' oder 'add'
  selectionDialogExerciseToSwap: null,
  selectionDialogInsertionIndex: 0,
  props: ["initialHeight"],
  data() {
    return {
      finalSet: null,
      tooltipData: null,
      showPRAnimation: false,
      prAnimationData: {
        recordType: "",
        newValue: "",
      },
      editingNote: false,
      editableNote: "",
      slideIndex: 0,
      intensityTechniqueMapping: intensityTechniqueMapping,
      isIntensityAlertExpanded: false,
      isFirstSetBeingAdded: false,
      previousScrollTop: 0,
      previousTbodyHeight: 0,
      showFeedbackOverlay: false,
      isTimerActive: false,
      pendingSet: null,
      feedbackState: "rating",
      selectedDifficulty: null,
      weightSuggestions: [],
      adjustedWeight: null,
      doNotShowAgain: false,
      disableFeedbackForExercise: false,
      plannedRestDuration: 0,
      restTimerRemaining: 0,
      overtimeSeconds: 0,
      restTimerInterval: null,
      pauseStartTime: null,
      isRetrospective: false,
      historyViewIndex: 0,
      retrospectiveSetIndex: null,
      weightDeviationStatus: "on_track",
      showIncompleteDialog: false, // NEU
      isAddingWarmupSet: false,
      imageDialog: false,
      imageDialogSrc: "",
      exerciseDetailDialog: false, // Steuert die Sichtbarkeit des neuen Dialogs
      detailDialogExercise: null,
      dragState: {
        active: false, // Ist ein Drag-Vorgang aktiv?
        origin: null, // Von welcher Seite kommt der Drag ('left' oder 'right')
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        avatarExercise: null, // Welche Übung wird gerade gezogen?
        dropZoneActive: false, // Ist der Avatar über der Drop-Zone?
      },
      showWeightPropagationDialog: false,
      propagationData: null,
      propagationCallback: null,
    }
  },
  computed: {
    ...mapState("runWorkoutStore", ["selectedExerciseIndex", "activeSplit", "disabledFeedbackExercises", "completedSetsMap", "hubGroupedSessions"]),
    ...mapGetters("runWorkoutStore", ["getSelectedExercise", "getActiveSupersetSubExercise", "getCurrentSatzVorgaben", "hubGroupedExercises"]),
    isFeedbackRelevant() {
      if (!this.getSelectedExercise || (this.activeSplit && this.activeSplit.isHyrox)) return false
      return this.getSelectedExercise.set_exercise_settings?.some((s) => s.text === "Gewicht")
    },
    isWarmupBlocked() {
      return this.hasWarmup && this.completedSetsArray.length > 1
    },
    allExercises() {
      return [...this.warmupOnly, ...this.regularExercises]
    },
    nextExercise() {
      const currentIndex = this.allExercises.findIndex((ex) => ex.originalIndex === this.selectedExerciseIndex)
      if (currentIndex === -1 || currentIndex >= this.allExercises.length - 1) {
        return null
      }
      return this.allExercises[currentIndex + 1]
    },
    previousExercise() {
      const currentIndex = this.allExercises.findIndex((ex) => ex.originalIndex === this.selectedExerciseIndex)
      if (currentIndex <= 0) {
        return null
      }
      return this.allExercises[currentIndex - 1]
    },

    mainCardContainerStyle() {
      // Diese Property wird jetzt für den Container verwendet
      return {
        transform: this.dragState.active ? "scale(0.95)" : "scale(1)",
        filter: this.dragState.active ? "blur(4px)" : "none",
      }
    },
    dragAvatarStyle() {
      if (!this.dragState.active) return {}

      const x = this.dragState.currentX - this.dragState.startX
      // Y-Bewegung ist jetzt 0, der Avatar bleibt auf der gleichen Höhe
      const y = 0

      const scale = this.dragState.dropZoneActive ? 1.2 : 1

      return {
        top: `${this.dragState.startY}px`,
        left: `${this.dragState.startX}px`,
        transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
      }
    },
    isSensoProActive() {
      return this.getSelectedExercise?.title === "Senso Pro"
    },
    currentSensoProSet() {
      if (!this.isSensoProActive || !this.isTodaySelected) return null
      return this.completedSetsArray.length > 0 ? this.completedSetsArray[0] : null
    },
    historicalSensoProNotes() {
      if (!this.isSensoProActive || !this.historicalSessions) return []
      const notes = []
      this.historicalSessions.forEach((session) => {
        if (session.steps && session.steps[0] && Array.isArray(session.steps[0])) {
          const noteEntry = session.steps[0].find((s) => s.text === "note")
          if (noteEntry && noteEntry.value) {
            notes.push({ date: moment(session.date).format("DD.MM.YYYY"), note: noteEntry.value })
          }
        }
      })
      return notes
    },
    exerciseForInput() {
      if (this.getSelectedExercise?.isSuperset) {
        return this.getActiveSupersetSubExercise
      }
      return this.getSelectedExercise
    },
    // In runWorkoutExerciseList.vue -> computed

    detailCarouselItems() {
      const ex = this.detailDialogExercise
      if (!ex) return []

      const imgs = []

      // 1. Füge zuerst das Hauptbild hinzu, falls es existiert.
      if (ex.picture) {
        imgs.push({
          src: this.getImageUrl(ex.picture),
          description: "Hauptansicht",
        })
      }

      // 2. Füge dann alle Bilder aus der Anleitung hinzu, aber nur, wenn sie sich vom Hauptbild unterscheiden.
      if (Array.isArray(ex.manual) && ex.manual.length > 0) {
        ex.manual.forEach((m, index) => {
          if (m.picture) {
            const manualImgSrc = this.getImageUrl(m.picture)
            // Verhindere Duplikate, falls das Hauptbild auch das erste Manual-Bild ist.
            if (!imgs.some((img) => img.src === manualImgSrc)) {
              imgs.push({
                src: manualImgSrc,
                description: m.description || `Schritt ${index + 1}`,
              })
            }
          }
        })
      }

      // 3. Fallback: Wenn nach allem immer noch kein Bild da ist, zeige den Placeholder.
      if (imgs.length === 0) {
        imgs.push({ src: "/img/einmalig/imageUploadPlaceholder.jpg", description: "Kein Bild verfügbar" })
      }

      return imgs
    },
    isTodaySelected() {
      return this.historyViewIndex === 0
    },
    incompleteExercises() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []

      const incomplete = []
      this.activeSplit.exercises.forEach((ex, index) => {
        // Logic for supersets
        if (ex.isSuperset) {
          ex.exercises.forEach((subEx) => {
            const completedSets = this.getCompletedSetsForSubExercise(ex, subEx)
            const plannedCount = subEx.sets ? subEx.sets.length : 0
            if (completedSets.length < plannedCount) {
              incomplete.push({
                title: `${subEx.title} (Supersatz)`,
                completed: completedSets.length,
                planned: plannedCount,
              })
            }
          })
          return
        }

        // Logic for single exercises
        // Access the map directly for reactivity
        const completedSets = this.completedSetsMap[index] || []
        const realCompletedCount = completedSets.filter((set) => !set.skipped).length

        let plannedCount = ex.sets.length
        if (ex.warmup) {
          plannedCount += 1
        }

        if (realCompletedCount < plannedCount) {
          incomplete.push({
            title: ex.title,
            completed: realCompletedCount,
            planned: plannedCount,
          })
        }
      })
      return incomplete
    },
    currentExerciseHistory() {
      if (!this.getSelectedExercise) return null
      return this.hubGroupedExercises.find((ex) => ex.exercise._id === this.getSelectedExercise._id)
    },
    historicalSessions() {
      if (this.activeSplit?.isHyrox) {
        if (!this.hubGroupedSessions || this.selectedExerciseIndex === null) return []

        const history = []
        const currentSegmentIndex = this.selectedExerciseIndex
        // 1. Finde alle vergangenen Rennen mit dem exakt gleichen Titel
        const pastRaces = this.hubGroupedSessions.filter(
          (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments
        )
        pastRaces.forEach((race) => {
          const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === currentSegmentIndex)
          if (pastSegment) {
            // Erstelle einen "Session"-Eintrag, der mit setsToDisplay kompatibel ist
            history.push({
              date: race.execution.createdAt,
              execution: race.execution,
              steps: [{ ...pastSegment }], // Wandle Segment in einen "step" um
            })
          }
        })
        return history
      }
      if (!this.currentExerciseHistory || !this.currentExerciseHistory.sets) return []
      return this.currentExerciseHistory.sets.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    setsToDisplay() {
      if (this.historyViewIndex === 0) {
        return this.completedSetsArray
      }
      const historicalSession = this.historicalSessions[this.historyViewIndex - 1]
      if (historicalSession && historicalSession.steps) {
        return historicalSession.steps.map((step) => {
          const setArray = []

          // --- START DER NEUEN, ROBUSTEN LOGIK ---
          // Prüft auf das neue Hyrox-Format
          if (step.duration != null) {
            setArray.push({ text: "Dauer", value: Number(step.duration).toFixed(2) })
          }
          if (step.weight != null) {
            setArray.push({ text: "Gewicht", value: step.weight })
          }
          if (step.reps != null && step.reps > 0) {
            // Nur anzeigen, wenn Reps vorhanden sind (z.B. Wall Balls)
            setArray.push({ text: "Wiederholungen", value: step.reps })
          }
          if (step.distance != null && step.distance > 0) {
            // Nur anzeigen, wenn Distanz vorhanden ist
            setArray.push({ text: "Distanz", value: step.distance })
          }

          // Fallback für das ganz alte Zeitformat
          if (step.time != null) {
            setArray.push({ text: "Dauer", value: Number(step.time).toFixed(2) })
          }
          // --- ENDE DER NEUEN LOGIK ---

          return setArray
        })
      }
      return []
    },
    historyTitle() {
      if (this.historyViewIndex === 0) {
        return "Heutiges Training"
      }
      const session = this.historicalSessions[this.historyViewIndex - 1]
      if (session) {
        // Gibt für die Anzeige den vollen Titel zurück, z.B. "HYROX (Men Pro)"
        return `Training vom ${this.$moment(session.date).format("DD.MM.YYYY")}`
      }
      return "Verlauf"
    },
    warmupOnly() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const completedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die auch Aufwärmsätze sind
          const completedWarmupCount = completedSets.filter((s) => s.isWarmup && !s.skipped).length
          return { ...ex, originalIndex: i, completedSetCount: completedWarmupCount }
        })
        .filter((ex) => !ex.isSuperset && ex.sets.length === 0 && ex.warmup)
    },

    regularExercises() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const allCompletedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die KEINE Aufwärmsätze sind
          const completedRegularCount = allCompletedSets.filter((s) => !s.isWarmup && !s.skipped).length
          const plannedSetCount = ex.isSuperset ? ex.exercises[0]?.sets?.length || 0 : ex.sets?.length || 0

          return {
            ...ex,
            originalIndex: i,
            completedSetCount: completedRegularCount, // Hier wird die korrigierte Anzahl verwendet
            plannedSetCount: plannedSetCount,
          }
        })
        .filter((ex) => {
          if (ex.isSuperset) return true
          return !(ex.sets.length === 0 && ex.warmup)
        })
    },
    selectedExerciseImageCarousel() {
      const ex = this.getSelectedExercise
      if (!ex) return []
      const imgs = []
      if (Array.isArray(ex.manual)) {
        ex.manual.forEach((m) => {
          if (m.picture) imgs.push({ src: this.getImageUrl(m.picture), description: m.description })
        })
      } else if (ex.picture) {
        imgs.push({ src: this.getImageUrl(ex.picture) })
      }
      return imgs
    },
    hasWarmup() {
      return !!this.getSelectedExercise?.warmup
    },
    warmupSettings() {
      if (!this.getSelectedExercise || !this.getSelectedExercise.warmup) return []
      let ws = this.getSelectedExercise.warmup.filter((s) => typeof s.value !== "undefined") || []
      const s = []
      const w = ws.find((s) => s.text === "Gewicht")
      if (w) s.push({ label: "Gewicht", value: w.value + "kg" })
      const minR = ws.find((s) => s.text === "Min Wiederholungen")
      const maxR = ws.find((s) => s.text === "Max Wiederholungen")
      if (minR && maxR) {
        s.push({ label: "WH", value: `${minR.value} - ${maxR.value}` })
      } else if (minR) {
        s.push({ label: "WH", value: minR.value })
      } else if (maxR) {
        s.push({ label: "WH", value: maxR.value })
      }
      const d = ws.find((s) => s.text === "Dauer")
      if (d) s.push({ label: "Dauer", value: d.value + " Minuten" })
      return s
    },
    currentPlanSettingsRaw() {
      // Die gesamte Logik steckt bereits in unserem Vuex-Getter.
      // Wir müssen ihn hier nur zurückgeben.
      return this.getCurrentSatzVorgaben || []
    },

    viewedSlidePresets() {
      const ex = this.getSelectedExercise
      if (!ex || ex.isSuperset) return [] // Für Supersätze ist die Slide-Navigation ohnehin deaktiviert

      // Fall 1: Aufwärmsatz wird angezeigt
      if (this.hasWarmup && this.slideIndex === 0) {
        return ex.warmup || []
      }

      // Fall 2: Ein regulärer Satz wird angezeigt
      // Wir passen den Index an, falls ein Aufwärmsatz existiert
      const regularSetIndex = this.hasWarmup ? this.slideIndex - 1 : this.slideIndex

      if (ex.sets && regularSetIndex >= 0 && regularSetIndex < ex.sets.length) {
        return ex.sets[regularSetIndex]
      }

      // Fallback, wenn keine passenden Vorgaben gefunden werden
      return []
    },
    displayPlanSettings() {
      const currentSetSettings = this.viewedSlidePresets // NEU: Nutzt die korrekten Vorgaben

      if (!Array.isArray(currentSetSettings) || currentSetSettings.length === 0) {
        return []
      }

      const settings = []

      const distance = currentSetSettings.find((s) => s.text === "Distanz")
      if (distance?.value) {
        settings.push({ label: "Distanz", value: distance.value + "m" })
      }

      const weight = currentSetSettings.find((s) => s.text === "Gewicht")
      if (weight?.value) {
        settings.push({ label: "Gewicht", value: weight.value + "kg" })
      }

      const minRep = currentSetSettings.find((s) => s.text === "Min Wiederholungen")?.value
      const maxRep = currentSetSettings.find((s) => s.text === "Max Wiederholungen")?.value
      if (minRep && maxRep) {
        settings.push({ label: "WH", value: `${minRep} - ${maxRep}` })
      } else if (minRep || maxRep) {
        settings.push({ label: "WH", value: minRep || maxRep })
      }

      const duration = currentSetSettings.find((s) => s.text === "Dauer")
      if (duration?.value) {
        settings.push({ label: "Dauer", value: duration.value + "s" })
      }

      const pauseSetting = currentSetSettings.find((s) => s.text === "Pause")
      if (pauseSetting?.value > 0) {
        settings.push({ label: "Pause", value: pauseSetting.value + "s" })
      }

      return settings
    },

    // KORREKTUR 3: Macht die Slide-Navigation Supersatz-fähig (obwohl sie ausgeblendet ist)
    totalSlides() {
      if (!this.getSelectedExercise) return 0
      if (this.getSelectedExercise.isSuperset) {
        // Bei einem Supersatz entspricht die Anzahl der "Runden" der Satzanzahl der ersten Sub-Übung
        return this.getSelectedExercise.exercises[0]?.sets?.length || 0
      }
      const c = (this.getSelectedExercise.sets || []).length
      return this.hasWarmup ? c + 1 : c
    },

    // KORREKTUR 4: Zeigt den korrekten Label für den nächsten Satz an
    slideLabel() {
      if (!this.getSelectedExercise) return ""

      if (this.getSelectedExercise.isSuperset) {
        const subEx = this.getActiveSupersetSubExercise
        if (!subEx) return "Supersatz"

        // Zähle, wie viele Sätze für DIESE Sub-Übung schon gemacht wurden
        const completedForThisSubEx = this.getCompletedSetsForSubExercise(this.getSelectedExercise, subEx).length
        return `Vorgabe Satz ${completedForThisSubEx + 1}`
      }

      // Bestehende Logik für Einzelübungen
      if (this.hasWarmup && this.slideIndex === 0) return "🔥 Aufwärmsatz Vorgaben"
      const i = this.hasWarmup ? this.slideIndex : this.slideIndex + 1
      return `Vorgaben Satz ${i}`
    },
    addSetLabel() {
      if (!this.getSelectedExercise) return "Satz hinzufügen"
      if (this.hasWarmup && this.completedSetsArray.length === 0) return "Aufwärmsatz hinzufügen"
      const nI = this.completedSetsArray.length + (this.hasWarmup ? 0 : 1)
      return `Satz ${nI} hinzufügen`
    },
    completedSets() {
      return this.completedSetsArray.length
    },
    completedSetsArray() {
      if (this.selectedExerciseIndex === null) return []
      return this.completedSetsMap[this.selectedExerciseIndex] || []
    },
    currentInputComponent() {
      if (!this.exerciseForInput) return null

      if (this.activeSplit.isHyrox) {
        return "HyroxInput"
      }
      if (this.exerciseForInput.title === "Senso Pro") {
        return "SensoProInput"
      }

      if (this.exerciseForInput.set_exercise_settings?.some((s) => s.text === "Distanz")) {
        return "DistanceRepInput"
      }

      if (this.exerciseForInput.set_exercise_settings?.some((s) => s.text === "Dauer")) {
        return "TimeInput"
      }

      return "WeightRepInput"
    },
    selectedExerciseMethodDetail() {
      if (this.getSelectedExercise && this.getSelectedExercise.method && this.intensityTechniqueMapping[this.getSelectedExercise.method]) {
        return this.intensityTechniqueMapping[this.getSelectedExercise.method]
      }
      return null
    },
    restTimerProgress() {
      // Wenn die Zeit abgelaufen ist oder keine Pause geplant war, ist der Kreis leer.
      if (this.plannedRestDuration <= 0 || this.restTimerRemaining <= 0) {
        return 0
      }
      // Berechne den prozentualen Anteil der *verbleibenden* Zeit.
      return (this.restTimerRemaining / this.plannedRestDuration) * 100
    },
    restTimerProgressColor() {
      // Wenn in Überzeit, eine andere Farbe anzeigen (z.B. rot)
      if (this.overtimeSeconds > 0) {
        return "#E53935" // Ein kräftiges Rot
      }
      // Wenn die Zeit abgelaufen ist (aber keine Überzeit)
      if (this.restTimerRemaining <= 0) {
        return "#4CAF50" // Dein Erfolgs-Grün
      }
      // In den letzten 10 Sekunden
      if (this.restTimerRemaining <= 10) {
        return "#FB8C00" // Dein Warn-Orange
      }
      // Standardfarbe
      return this.selectedExerciseMethodDetail?.color || "primary"
    },
    isCountdownTextTooLong() {
      const seconds = this.restTimerRemaining > 0 ? this.restTimerRemaining : this.overtimeSeconds
      return String(seconds).length >= 3
    },
    isFeedbackCompleted() {
      if (this.doNotShowAgain) return true
      if (this.feedbackState === "rating") return !!this.selectedDifficulty
      if (this.feedbackState === "show_weights") return this.adjustedWeight !== null
      return false
    },
    buttonColor() {
      if (!this.isFeedbackCompleted) return "blue-grey darken-1"
      if (this.doNotShowAgain) return "primary"
      const colors = { easy: "green", medium: "orange", hard: "red" }
      return colors[this.selectedDifficulty] || "blue-grey darken-1"
    },
    buttonText() {
      return this.isTimerActive ? "Weiter" : "Bestätigen"
    },
    buttonIcon() {
      return this.isTimerActive ? "mdi-skip-next" : "mdi-check"
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", [
      "setSelectedExerciseIndex",
      "addCompletedSet",
      "removeCompletedSet",
      "clearCompletedSets",
      "addDisabledFeedbackExercise",
      "replaceCompletedSet",
      "updateCompletedSet",
      "swapExerciseTemporarily", // NEU
      "addExerciseTemporarily", // NEU
      "updateFutureExerciseWeights",
      "checkForPersonalRecord",
    ]),
    getHyroxCategoryDetails(title) {
      if (!title) return { icon: null, text: "" }
      if (title.includes("Men Pro")) return { icon: "mdi-crown", text: "Men Pro" }
      if (title.includes("Women Pro")) return { icon: "mdi-crown-outline", text: "Women Pro" }
      if (title.includes("Men")) return { icon: "mdi-human-male", text: "Men" }
      if (title.includes("Women")) return { icon: "mdi-human-female", text: "Women" }
      if (title.includes("Üben")) return { icon: "mdi-dumbbell", text: "Freies Training" }
      return { icon: null, text: title }
    },
    redoWarmup() {
      if (this.hasWarmup && this.completedSetsArray.length === 1 && this.completedSetsArray[0].skipped) {
        this.removeCompletedSet({ idx: this.selectedExerciseIndex, setPos: 0 })
      }
    }, // In runWorkoutExerciseList.vue -> methods
    promptForWeightPropagation(weightForPropagation) {
      const ex = this.getSelectedExercise
      const exerciseIndex = this.selectedExerciseIndex

      const completedRegularSetsCount = this.completedSetsArray.filter((s) => !s.isWarmup && !s.skipped).length
      let plannedSetCount = 0
      if (ex.isSuperset) {
        plannedSetCount = ex.exercises[0]?.sets?.length || 0
      } else {
        plannedSetCount = ex.sets.length
      }
      if (completedRegularSetsCount >= plannedSetCount) {
        this.clearRestTimer()
        return
      }

      const lastCompletedSetIndex = this.completedSetsArray.length - 1
      const plannedWeightOfCompletedSet = this.getPlannedWeightForSet(lastCompletedSetIndex)

      if (plannedWeightOfCompletedSet === null) {
        this.clearRestTimer()
        return
      }

      // --- START DER KORREKTUR: Alle Werte in Zahlen umwandeln ---
      const performedWeightNumber = Number(weightForPropagation)
      const plannedWeightNumber = Number(plannedWeightOfCompletedSet)

      const difference = performedWeightNumber - plannedWeightNumber

      const remainingSets = ex.sets.slice(completedRegularSetsCount)
      const plannedWeightsOfRemainingSets = remainingSets
        .map((set) => Number(set.find((s) => s.text === "Gewicht")?.value)) // Wert direkt in eine Zahl umwandeln
        .filter((w) => !isNaN(w)) // Sicherstellen, dass nur gültige Zahlen bleiben
      // --- ENDE DER KORREKTUR ---

      if (plannedWeightsOfRemainingSets.length === 0 && !ex.isSuperset) {
        this.clearRestTimer()
        return
      }

      const firstPlannedWeightOfRemaining = plannedWeightsOfRemainingSets[0]
      const areRemainingWeightsUniform = plannedWeightsOfRemainingSets.every((w) => w === firstPlannedWeightOfRemaining)
      const isIncrease = difference > 0

      if (areRemainingWeightsUniform && firstPlannedWeightOfRemaining === plannedWeightNumber) {
        this.propagationData = {
          isIncrease: isIncrease,
          type: "absolute",
          oldValue: `${firstPlannedWeightOfRemaining} kg`,
          newValue: `${performedWeightNumber} kg`,
          description: "Soll das neue Gewicht für alle Folgesätze übernommen werden?",
        }
        this.propagationCallback = () =>
          this.updateFutureExerciseWeights({
            exerciseIndex,
            startingFromSetIndex: completedRegularSetsCount,
            type: "absolute",
            value: performedWeightNumber,
          })
      } else {
        const diffText =
          difference >= 0
            ? `um ${Math.round(difference * 10) / 10} kg zu erhöhen`
            : `um ${Math.abs(Math.round(difference * 10) / 10)} kg zu reduzieren`

        const oldValuesForDisplay = plannedWeightsOfRemainingSets.slice(0, 3)
        // KORREKTUR: Sicherstellen, dass hier mit Zahlen gerechnet wird
        const newValuesForDisplay = oldValuesForDisplay.map((w) => Math.max(0, w + difference))
        this.propagationData = {
          isIncrease: isIncrease,
          type: "relative",
          oldValue: oldValuesForDisplay,
          newValue: difference >= 0 ? `+${difference}` : `${difference}`,
          newValueCalculated: newValuesForDisplay,
          description: "Sollen die Gewichte der Folgesätze angepasst werden?",
          subDescription: `Dein Plan wird ${diffText}.`,
        }
        this.propagationCallback = () =>
          this.updateFutureExerciseWeights({ exerciseIndex, startingFromSetIndex: completedRegularSetsCount, type: "relative", value: difference })
      }

      this.showWeightPropagationDialog = true
    },
    executePropagation() {
      if (typeof this.propagationCallback === "function") {
        this.propagationCallback()
      }
      this.closePropagationDialog()
    },
    async closePropagationDialog() {
      this.showWeightPropagationDialog = false
      // Kurze Verzögerung, damit die Dialog-Animation sauber aussieht
      setTimeout(() => {
        this.propagationData = null
        this.propagationCallback = null
        // Wichtig: Erst hier den Timer-Overlay komplett schliessen
        this.clearRestTimer()
      }, 300)

      try {
        if (this.activeSplit && !this.activeSplit.isHyrox) {
          const recordCheckResult = await this.checkForPersonalRecord({
            exercise: this.getSelectedExercise, // Wir übergeben das ganze Übungsobjekt
            completedSet: this.finalSet,
          })

          if (recordCheckResult.isNewRecord) {
            this.prAnimationData = {
              recordType: recordCheckResult.type,
              newValue: recordCheckResult.newValue,
            }
            this.showPRAnimation = true
            // Optional, aber empfohlen für den extra Kick:
            this.triggerConfetti()
          }
        }
      } catch (error) {
        console.error("Fehler bei der Rekord-Prüfung:", error)
      }
    },
    dragStart(origin, e) {
      this.dragState.origin = origin
      this.dragState.avatarExercise = origin === "left" ? this.previousExercise : this.nextExercise
      if (!this.dragState.avatarExercise) return

      const handleEl = e.currentTarget
      const rect = handleEl.getBoundingClientRect()

      this.dragState.active = true
      // Wir benötigen nur die X-Startposition
      this.dragState.startX = rect.left + rect.width / 2
      // Die Y-Position wird fixiert, hier auf der Höhe des Handles
      this.dragState.startY = rect.top + rect.height / 2

      const initialPos = e.type === "touchstart" ? e.touches[0] : e
      this.dragState.currentX = initialPos.clientX

      window.addEventListener("mousemove", this.dragMove)
      window.addEventListener("touchmove", this.dragMove, { passive: false })
      window.addEventListener("mouseup", this.dragEnd)
      window.addEventListener("touchend", this.dragEnd)
    },

    dragMove(e) {
      if (!this.dragState.active) return
      e.preventDefault()

      const pos = e.type === "touchmove" ? e.touches[0] : e
      // Wir aktualisieren nur noch die X-Position
      this.dragState.currentX = pos.clientX

      const dropZoneEl = this.$refs.dropZone
      if (dropZoneEl) {
        const rect = dropZoneEl.getBoundingClientRect()
        this.dragState.dropZoneActive =
          pos.clientX > rect.left &&
          pos.clientX < rect.right &&
          pos.clientY > rect.top && // Y-Check bleibt für die Aktivierung relevant
          pos.clientY < rect.bottom
      }
    },

    dragEnd() {
      if (!this.dragState.active) return

      if (this.dragState.dropZoneActive) {
        this.triggerExerciseChange()
      } else {
        // Zurücksnappen
        this.resetDragState()
      }

      // Event Listeners entfernen
      window.removeEventListener("mousemove", this.dragMove)
      window.removeEventListener("touchmove", this.dragMove)
      window.removeEventListener("mouseup", this.dragEnd)
      window.removeEventListener("touchend", this.dragEnd)
    },

    triggerExerciseChange() {
      const dropZoneEl = this.$refs.dropZone
      const rect = dropZoneEl.getBoundingClientRect()

      // Avatar zur Mitte der Drop-Zone animieren und expandieren
      const avatarEl = document.querySelector(".drag-avatar")
      if (avatarEl) {
        const targetX = rect.left + rect.width / 2 - this.dragState.startX
        const targetY = rect.top + rect.height / 2 - this.dragState.startY
        avatarEl.style.transition = "transform 0.4s cubic-bezier(0.5, 0, 0.1, 1), opacity 0.4s ease"
        avatarEl.style.transform = `translate3d(${targetX}px, ${targetY}px, 0) scale(20)`
        avatarEl.style.opacity = "0"
      }

      // Warten bis die Animation fertig ist
      setTimeout(() => {
        this.open(this.dragState.avatarExercise)
        this.resetDragState()
      }, 400)
    },

    resetDragState() {
      this.dragState = {
        active: false,
        origin: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        avatarExercise: null,
        dropZoneActive: false,
      }
    },
    getPlanForSetAtIndex(setIndex) {
      const mainExercise = this.getSelectedExercise
      if (!mainExercise || setIndex < 0) return []

      // Logik für Supersätze
      if (mainExercise.isSuperset) {
        // Finde heraus, welche Sub-Übung beim N-ten Satz des Supersatzes dran ist
        const subExIndex = setIndex % mainExercise.exercises.length
        const subEx = mainExercise.exercises[subExIndex]
        // Finde heraus, der wievielte Satz es für diese Sub-Übung ist
        const planIndexForSubEx = Math.floor(setIndex / mainExercise.exercises.length)
        return subEx.sets[planIndexForSubEx] || []
      }

      // Logik für Einzelübungen
      else {
        const hasWarmup = mainExercise.warmup && mainExercise.warmup.length > 0
        // Wenn es der erste Satz ist und ein Warmup existiert, nimm die Warmup-Vorgaben
        if (hasWarmup && setIndex === 0) {
          return mainExercise.warmup
        }
        // Ansonsten berechne den korrekten Index für die regulären Sätze
        const regularSetIndex = hasWarmup ? setIndex - 1 : setIndex
        if (regularSetIndex < 0) return []

        return mainExercise.sets[regularSetIndex] || []
      }
    },
    getCardStyle(exercise) {
      if (exercise.method && this.intensityTechniqueMapping[exercise.method]) {
        const color = this.intensityTechniqueMapping[exercise.method].color
        // Wir setzen eine CSS-Variable, die im Style-Block verwendet wird
        return { "--glow-color": color }
      }
      return {}
    },
    getCompletedSetsForSubExercise(superset, subExercise) {
      if (!superset || !subExercise) return []

      // Greife auf das Array aller Sätze für diesen Supersatz-Block zu
      // Wir verwenden die direkt gemappte `completedSetsMap`
      const allSetsForSuperset = this.completedSetsMap[superset.originalIndex] || []

      // Filtere die Sätze, die zur spezifischen Sub-Übung gehören,
      // indem du die 'subExerciseId' prüfst, die wir im Store hinzufügen.
      return allSetsForSuperset.filter((set) => set.subExerciseId === subExercise._id)
    },
    // NEUE METHODEN
    openSwapDialog() {
      this.selectionDialogMode = "swap"
      this.selectionDialogExerciseToSwap = this.getSelectedExercise
      this.isSelectionDialogActive = true
    },
    openAddDialog(index) {
      this.$emit("open-add-dialog", index)
    },
    handleSwapSelected({ newExercise, exerciseIndex }) {
      this.swapExerciseTemporarily({ newExercise, exerciseIndex })
    },
    handleAddSelected({ newExercise, atIndex }) {
      // Erstellt ein leeres Set-Array für die neue Übung
      const exerciseWithEmptySets = {
        ...newExercise,
        sets: [],
        warmup: null, // oder Standard-Warmup definieren falls gewünscht
      }
      this.addExerciseTemporarily({ newExercise: exerciseWithEmptySets, atIndex })

      // Optional: Direkt zur neu hinzugefügten Übung wechseln
      this.setSelectedExerciseIndex(atIndex)
    },
    promptFinishWorkout() {
      if (this.incompleteExercises.length > 0) {
        this.showIncompleteDialog = true
      } else {
        this.$router.push({ name: "WorkoutSummary" })
      }
    },

    finishAnyway() {
      this.showIncompleteDialog = false
      this.$router.push({ name: "WorkoutSummary" })
    },
    showInfoView() {
      this.feedbackState = "info"
    },
    hideInfoView() {
      this.feedbackState = "rating"
    },
    skipFeedback() {
      this.finishFeedbackAndCloseOverlay(true)
    },
    async finishFeedbackAndCloseOverlay(skipFeedback = false) {
      if (!this.pendingSet) return

      const performedWeight = this.pendingSet.find((s) => s.text === "Gewicht")?.value
      const suggestedNewWeight = this.adjustedWeight
      const deviationStatus = this.weightDeviationStatus

      let finalSet = [...this.pendingSet]

      if (!skipFeedback) {
        if (this.selectedDifficulty) {
          finalSet.push({ text: "Difficulty", value: this.selectedDifficulty })
        }
        // WICHTIG: Hier wird deine finale Entscheidung für das nächste Training gespeichert.
        if (suggestedNewWeight !== null) {
          finalSet.push({ text: "newWeight", value: suggestedNewWeight })
        }
        if (this.doNotShowAgain) {
          this.addDisabledFeedbackExercise(this.getSelectedExercise._id)
        }
      }

      if (this.isTimerActive) {
        const actualDuration = this.pauseStartTime ? Math.round((Date.now() - this.pauseStartTime) / 1000) : 0
        finalSet.push({ text: "Tatsächliche Pause", value: actualDuration })
      }

      if (this.isRetrospective) {
        this.updateCompletedSet({
          exerciseIndex: this.selectedExerciseIndex,
          setIndex: this.retrospectiveSetIndex,
          updatedSet: finalSet,
        })
      } else {
        this.addCompletedSet({
          set: finalSet,
          isWarmup: this.isAddingWarmupSet,
        })
      }

      // HIER IST DIE KORREKTUR: Die Logik prüft jetzt auf BEIDE Arten von Änderungen.
      const wasPerformedDifferently = deviationStatus === "higher" || deviationStatus === "lower"
      const wasAdjustedManually = suggestedNewWeight !== null
      const shouldPromptForPropagation = performedWeight !== null && (wasPerformedDifferently || wasAdjustedManually)

      if (shouldPromptForPropagation) {
        // Die 'weightForPropagation' ist IMMER deine finale Entscheidung (was du manuell gewählt hast),
        // ODER, falls du nichts gewählt hast, was du tatsächlich gehoben hast
        this.finalSet = finalSet
        const weightForPropagation = suggestedNewWeight !== null ? suggestedNewWeight : performedWeight
        this.$nextTick(() => {
          this.promptForWeightPropagation(weightForPropagation)
        })
      } else {
        try {
          if (this.activeSplit && !this.activeSplit.isHyrox) {
            const recordCheckResult = await this.checkForPersonalRecord({
              exercise: this.getSelectedExercise, // Wir übergeben das ganze Übungsobjekt
              completedSet: finalSet,
            })

            if (recordCheckResult.isNewRecord) {
              this.prAnimationData = {
                recordType: recordCheckResult.type,
                newValue: recordCheckResult.newValue,
              }
              this.showPRAnimation = true
              // Optional, aber empfohlen für den extra Kick:
              this.triggerConfetti()
            }
          }
        } catch (error) {
          console.error("Fehler bei der Rekord-Prüfung:", error)
        }
        this.clearRestTimer()
      }

      this.$nextTick(async () => {
        const lastRow = this.$refs.dialogContent?.querySelector(".completed-table tbody tr:last-child")
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      })
    },

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      // Fall 1: Das gehobene Gewicht weicht vom Plan ab (z.B. 85kg statt 80kg)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        const difference = deviation.actual - deviation.planned
        const tooltipData = {
          type: "adjustment",
          title: isIncrease ? "Gewichts-Steigerung" : "Gewichts-Anpassung",
          icon: isIncrease ? "🚀" : "🛠️",
          color: isIncrease ? "primary" : "orange",
          isIncrease: isIncrease,
          details: [
            { label: "Geplant", value: `${deviation.planned} kg` },
            { label: "Ausgeführt", value: `${deviation.actual} kg` },
            { label: "Differenz", value: `${difference > 0 ? "+" : ""}${difference} kg` },
          ],
        }
        // HIER IST DIE KORREKTUR: Prüfe, ob TROTZDEM eine andere Zukunfts-Planung gemacht wurde.
        if (newWeightEntry) {
          tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
        }
        return tooltipData
      }

      // Fall 2: Das gehobene Gewicht war korrekt, ABER es wurde eine Bewertung abgegeben.
      if (difficultyEntry) {
        let tooltipData
        switch (difficultyEntry.value) {
          case "hard":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Schwer",
              icon: "🥵",
              color: "#EF5350",
              details: "Du hast angegeben, dass dieser Satz extrem fordernd war und die letzten Wiederholungen kaum zu schaffen waren.",
            }
            break
          case "medium":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Perfekt",
              icon: "🙂",
              color: "#FFCA28",
              details: "Deine Bewertung zeigt, dass der Satz fordernd, aber mit guter Form machbar war. Genau richtig!",
            }
            break
          case "easy":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Leicht",
              icon: "😎",
              color: "#66BB6A",
              details: "Dieser Satz war einfach für dich. Du hättest deutlich mehr Wiederholungen oder Gewicht bewältigen können.",
            }
            break
        }
        // Auch hier prüfen wir, ob eine Zukunfts-Planung gemacht wurde.
        if (newWeightEntry) {
          tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
        }
        return tooltipData
      }

      // Fallback, falls heute noch keine Bewertung abgegeben wurde
      if (this.isTodaySelected) {
        return {
          type: "rating",
          title: "Feedback fehlt",
          icon: "💬",
          color: "grey",
          details: "Bewerte diesen Satz, um deinem Coach Feedback zu geben und deinen Plan zu optimieren.",
        }
      }

      return null
    },
    open(ex, ev) {
      this.historyViewIndex = 0
      this.setSelectedExerciseIndex(ex.originalIndex)
      this.slideIndex = 0
      this.isIntensityAlertExpanded = false
      this.clearRestTimer()
      this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(ex._id)
    },
    navigateHistory(direction) {
      const newIndex = this.historyViewIndex + direction
      if (newIndex >= 0 && newIndex <= this.historicalSessions.length) {
        this.historyViewIndex = newIndex
      }
    },
    prevSlide() {
      if (this.slideIndex > 0) this.slideIndex--
    },
    nextSlide() {
      if (this.slideIndex < this.totalSlides - 1) this.slideIndex++
    },

    handleAddSet(set) {
      if (!this.getSelectedExercise) return

      this.isAddingWarmupSet = this.hasWarmup && this.completedSetsArray.length === 0
      this.pendingSet = set

      // Für übersprungene Sätze wird das Feedback immer übersprungen.
      // Für alle anderen Sätze entscheidet triggerFeedbackFlow, was zu tun ist.
      if (set.some((s) => s.skipped)) {
        this.finishFeedbackAndCloseOverlay(true)
      } else {
        this.triggerFeedbackFlow()
      }
    },
    onRemoveSet(idx) {
      if (this.hasWarmup && idx === 0) {
        const placeholder = { skipped: true, type: "warmup", id: Date.now(), showMenu: false }
        this.replaceCompletedSet({ idx: this.selectedExerciseIndex, setPos: 0, newSet: placeholder })
      } else {
        this.removeCompletedSet({ idx: this.selectedExerciseIndex, setPos: idx })
      }
    },
    promptDeleteSet(idx, set) {
      if (this.isWarmupBlocked && idx === 0) {
        alert("Du kannst das Warmup nicht mehr löschen, nachdem du mit dem Training begonnen hast.")
        return
      }
      if (this.hasWarmup && idx === 0) return
      if (set.skipped) return
      if (window.confirm(`Möchtest du den Satz ${this.hasWarmup ? (idx === 0 ? "Aufwärm" : this.realSetNumber(idx)) : idx + 1} wirklich löschen?`)) {
        this.onRemoveSet(idx)
      }
    },
    formatSetText(set) {
      if (!Array.isArray(set)) return ""
      if (set.skipped) {
        return "Warmup übersprungen (klicken zum Wiederholen)"
      }
      // Alle möglichen Werte extrahieren
      const distance = set.find((e) => e.text === "Distanz")?.value
      const weight = set.find((e) => e.text === "Gewicht")?.value
      const reps = set.find((e) => /Wiederholungen/.test(e.text))?.value
      const duration = set.find((e) => e.text === "Dauer")?.value

      // --- START: NEUE LOGIK FÜR HYROX & NORMALE SÄTZE ---

      // Fall 1: Es gibt eine gemessene Dauer (typisch für Hyrox-Stationen)
      if (duration) {
        if (duration) {
          // Formatiert die Dauer in MM:SS
          const formatTime = (seconds) => {
            if (isNaN(seconds) || seconds <= 0) return "---"
            const min = Math.floor(seconds / 60)
            const sec = Math.floor(seconds % 60)
            return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
          }

          let mainText = `Time: ${formatTime(duration)}`
          const details = []

          // Fügt zusätzliche Details in Klammern hinzu
          if (weight) details.push(`${weight}kg`)
          if (reps) details.push(`${reps} Reps`)
          if (distance) details.push(`${distance}m`)

          if (details.length > 0) {
            mainText += ` (${details.join(" / ")})`
          }
          return mainText
        }

        // Fall 2: Keine Dauer, also ein normales Training (bestehende Logik)
        let mainText = ""
        if (distance) {
          mainText = `${distance}m`
          if (weight > 0 || weight !== null) {
            mainText += ` x ${weight}kg`
          }
        } else if (weight != null && reps != null) {
          mainText = `${weight} kg x ${reps}`
        } else if (reps != null) {
          mainText = `${reps} WH`
        }
        return mainText
      }

      // Fall 2: Keine Dauer, also ein normales Training
      let mainText = ""
      if (distance) {
        mainText = `${distance}m`
        if (weight > 0 || weight !== null) {
          mainText += ` x ${weight}kg`
        }
      } else if (weight != null && reps != null) {
        mainText = `${weight} kg x ${reps}`
      } else if (reps != null) {
        mainText = `${reps} WH`
      }
      return mainText
      // --- ENDE: NEUE LOGIK ---
    },
    getImageForExercise(ex) {
      if (ex.picture) return this.getImageUrl(ex.picture)
      if (ex.manual?.[0]?.picture) return this.getImageUrl(ex.manual[0].picture)
      return "/img/default-exercise.webp"
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      let fn = String(id)
      if (fn.includes("/")) {
        fn = fn.substring(fn.lastIndexOf("/") + 1)
      }
      const nWoE = fn.replace(/\.[^/.]+$/, "")
      if (!nWoE) return "/img/default-exercise.webp"
      return `/img/uebungen/${id.replace(/\.[^/.]+$/, "")}.webp`
    },
    isExerciseCompleted(exercise) {
      if (!exercise) return false

      if (exercise.title === "Senso Pro") {
        return exercise.completedSetCount > 0
      }

      // Logic for Supersets
      if (exercise.isSuperset) {
        // A superset is complete if all its sub-exercises are complete.
        return exercise.exercises.every((subEx) => {
          const completedSets = this.getCompletedSetsForSubExercise(exercise, subEx)
          const plannedCount = subEx.sets ? subEx.sets.length : 0
          return completedSets.length >= plannedCount
        })
      }

      if (exercise.sets.length === 0 && exercise.warmup) {
        // KORREKTUR: Hier wird 'completedSetsMap' statt 'getCompletedSets' verwendet.
        const warmupSet = (this.completedSetsMap[exercise.originalIndex] || [])[0]
        return warmupSet && !warmupSet.skipped
      }

      // Logic for regular exercises
      else if (exercise.plannedSetCount > 0) {
        return exercise.completedSetCount >= exercise.plannedSetCount
      }

      return false
    },
    toggleIntensityAlertExpansion() {
      this.isIntensityAlertExpanded = !this.isIntensityAlertExpanded
    },
    getSectionHeadlineColor(baseColor, isSecondary = false) {
      if (isSecondary) {
        return baseColor === "primary"
          ? "orange darken-1"
          : baseColor.includes("darken")
          ? baseColor.replace(/darken-\d/, "darken-3")
          : baseColor + " darken-2"
      }
      return baseColor.includes("lighten") ? baseColor.replace(/lighten-\d/, "") : baseColor.includes("darken") ? baseColor : baseColor + " darken-1"
    },
    getIconColorForItem(item, type) {
      if (type === "advantage") return "green accent-3"
      if (type === "reason") return "light-blue lighten-1"
      return "grey"
    },
    triggerConfetti() {
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors: ["#FFC107", "#FF9800", "#FFFFFF", "#FFD700"],
      })
    }, // In runWorkoutExerciseList.vue -> methods
    triggerFeedbackFlow() {
      // Bricht ab, wenn der User Feedback für diese Übung deaktiviert hat
      if ((this.disabledFeedbackExercises || []).includes(this.getSelectedExercise._id)) {
        this.finishFeedbackAndCloseOverlay(true)
        return
      }
      // 2. Prüfe, ob eine Pause geplant ist
      const planForCompletedSet = this.getPlanForSetAtIndex(this.completedSetsArray.length)
      const pauseSetting = planForCompletedSet.find((s) => s.text === "Pause")
      const plannedPause = pauseSetting && pauseSetting.value && Number(pauseSetting.value) > 0 ? Number(pauseSetting.value) : 0

      // 2. Wenn es ein Hyrox-Wettkampf ist, zeige NUR den Timer (falls vorhanden)
      if (this.activeSplit.isHyrox && this.activeSplit.isCompetition) {
        if (plannedPause === 0) {
          // Kein Timer -> Direkt speichern und schliessen
          this.finishFeedbackAndCloseOverlay(true)
          return
        } else {
          // Nur den Timer anzeigen
          this.showFeedbackOverlay = true
          this.isTimerActive = true
          this.plannedRestDuration = plannedPause
          this.pauseStartTime = Date.now()
          this.clearRestTimerInterval()
          this.restTimerInterval = setInterval(this.updateTimerDisplay, 1000)
          this.updateTimerDisplay()
          this.feedbackState = "timer_only" // Neutraler Zustand ohne Smileys
          return // Wichtig: Beendet die Funktion hier
        }
      }

      // --- START: NEUE INTELLIGENZ ---
      // 1. Analysiere den Satz-Typ
      const hasDistance = this.pendingSet.some((s) => s.text === "Distanz")
      const hasWeight = this.pendingSet.some((s) => s.text === "Gewicht")
      const isRepsOnly = this.pendingSet.some((s) => /Wiederholungen/.test(s.text)) && !hasWeight && !hasDistance

      // Die Bewertung soll nur bei Gewichtsübungen erscheinen
      const shouldShowRating = hasWeight
      // --- ENDE: NEUE INTELLIGENZ ---

      // 3. Entscheide, was zu tun ist
      if (!shouldShowRating && plannedPause === 0) {
        // Fall A: Keine Bewertung und kein Timer -> Direkt speichern und schliessen
        this.finishFeedbackAndCloseOverlay(true)
        return
      }

      // Fall B: Entweder eine Bewertung oder ein Timer (oder beides) wird benötigt -> Overlay anzeigen
      this.showFeedbackOverlay = true

      if (plannedPause > 0 && !this.isRetrospective) {
        this.isTimerActive = true
        this.plannedRestDuration = plannedPause
        this.pauseStartTime = Date.now()
        this.clearRestTimerInterval()
        this.restTimerInterval = setInterval(this.updateTimerDisplay, 1000)
        this.updateTimerDisplay()
      } else {
        this.isTimerActive = false
      }

      if (shouldShowRating) {
        // Normaler Flow mit Bewertung
        this.weightDeviationStatus = "on_track" // Zurücksetzen für die Anzeige
        const plannedWeight = planForCompletedSet.find((s) => s.text === "Gewicht")?.value
        const actualWeight = this.pendingSet.find((s) => s.text === "Gewicht")?.value
        if (plannedWeight !== null && actualWeight !== undefined) {
          if (actualWeight > plannedWeight) this.weightDeviationStatus = "higher"
          else if (actualWeight < plannedWeight) this.weightDeviationStatus = "lower"
        }
        this.feedbackState = "rating"
      } else {
        // Flow nur mit Timer: Wir zeigen keine Smileys an
        this.feedbackState = "timer_only" // Ein neutraler Zustand
      }
    },

    updateTimerDisplay() {
      if (!this.pauseStartTime) return

      const elapsedSeconds = Math.floor((Date.now() - this.pauseStartTime) / 1000)
      const remaining = this.plannedRestDuration - elapsedSeconds

      if (remaining > 0) {
        this.restTimerRemaining = remaining
        this.overtimeSeconds = 0
      } else {
        this.restTimerRemaining = 0
        this.overtimeSeconds = -remaining // oder Math.abs(remaining)
      }
    },

    // NEU: Diese Methode wird aufgerufen, wenn der Tab wieder sichtbar wird.
    handleVisibilityChange() {
      if (document.visibilityState === "visible" && this.isTimerActive) {
        this.updateTimerDisplay()
      }
    },

    clearRestTimerInterval() {
      if (this.restTimerInterval) {
        clearInterval(this.restTimerInterval)
        this.restTimerInterval = null
      }
    },
    clearRestTimer() {
      this.clearRestTimerInterval()
      this.showFeedbackOverlay = false
      this.isTimerActive = false
      this.plannedRestDuration = 0
      this.restTimerRemaining = 0
      this.overtimeSeconds = 0
      this.pauseStartTime = null
      this.pendingSet = null
      this.feedbackState = "rating"
      this.selectedDifficulty = null
      this.weightSuggestions = []
      this.adjustedWeight = null
      this.doNotShowAgain = false
      this.isRetrospective = false
      this.retrospectiveSetIndex = null
      this.weightDeviationStatus = "on_track"
      this.isAddingWarmupSet = false
    },
    handleSmileyClick(difficulty) {
      this.selectedDifficulty = difficulty
      const hasWeight = this.pendingSet.some((p) => p.text === "Gewicht")

      if (this.weightDeviationStatus === "higher") {
        if (difficulty === "easy" || difficulty === "hard") {
          this.showWeightSuggestions()
        } else {
          // this.finishFeedbackAndCloseOverlay()
        }
        return
      }

      if (difficulty === "medium" || !hasWeight) {
        //this.finishFeedbackAndCloseOverlay()
      } else {
        this.showWeightSuggestions()
      }
    },
    showWeightSuggestions() {
      const currentWeightEntry = this.pendingSet.find((p) => p.text === "Gewicht")
      if (!currentWeightEntry || isNaN(parseFloat(currentWeightEntry.value))) {
        this.finishFeedbackAndCloseOverlay()
        return
      }
      const currentWeight = parseFloat(currentWeightEntry.value)
      let suggestions = []
      if (this.selectedDifficulty === "hard") {
        suggestions = [
          Math.round((currentWeight * 0.95) / 2.5) * 2.5,
          Math.round((currentWeight * 0.9) / 2.5) * 2.5,
          Math.round((currentWeight * 0.85) / 2.5) * 2.5,
        ]
      } else {
        suggestions = [currentWeight + 2.5, currentWeight + 5.0, currentWeight + 7.5]
      }
      this.weightSuggestions = [...new Set(suggestions.filter((w) => w > 0 && w < currentWeight * 2))].sort((a, b) => a - b).slice(0, 3)
      if (this.weightSuggestions.length === 0) {
        this.feedbackState = "rating"
        return
      }
      this.adjustedWeight = this.weightSuggestions[0]
      this.feedbackState = "show_weights"
    },
    getSmileyForSet(set) {
      if (!Array.isArray(set)) {
        return { icon: null, color: null }
      }
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      if (!difficultyEntry) return { icon: "mdi-help-circle-outline", color: "grey" }
      const difficulty = difficultyEntry.value
      switch (difficulty) {
        case "hard":
          return { icon: "mdi-emoticon-dead", color: "red" }
        case "medium":
          return { icon: "mdi-emoticon-happy", color: "orange" }
        case "easy":
          return { icon: "mdi-weight-lifter", color: "green" }
        default:
          return { icon: "mdi-help-circle-outline", color: "grey" }
      }
    },

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      let tooltipData = null

      // SCHRITT 1: Bestimme das primäre Feedback (Abweichung oder Bewertung)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        const difference = deviation.actual - deviation.planned
        tooltipData = {
          type: "adjustment",
          title: isIncrease ? "Gewichts-Steigerung" : "Gewichts-Anpassung",
          icon: isIncrease ? "🚀" : "🛠️",
          color: isIncrease ? "primary" : "orange",
          isIncrease: isIncrease,
          details: [
            { label: "Geplant", value: `${deviation.planned} kg` },
            { label: "Ausgeführt", value: `${deviation.actual} kg` },
            { label: "Differenz", value: `${difference > 0 ? "+" : ""}${difference} kg` },
          ],
        }
      } else if (difficultyEntry) {
        switch (difficultyEntry.value) {
          case "hard":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Schwer",
              icon: "🥵",
              color: "#EF5350",
              details: "Du hast angegeben, dass dieser Satz extrem fordernd war und die letzten Wiederholungen kaum zu schaffen waren.",
            }
            break
          case "medium":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Perfekt",
              icon: "🙂",
              color: "#FFCA28",
              details: "Deine Bewertung zeigt, dass der Satz fordernd, aber mit guter Form machbar war. Genau richtig!",
            }
            break
          case "easy":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Leicht",
              icon: "😎",
              color: "#66BB6A",
              details: "Dieser Satz war einfach für dich. Du hättest deutlich mehr Wiederholungen oder Gewicht bewältigen können.",
            }
            break
        }
      } else if (this.isTodaySelected) {
        return {
          type: "rating",
          title: "Feedback fehlt",
          icon: "💬",
          color: "grey",
          details: "Bewerte diesen Satz, um deinem Coach Feedback zu geben und deinen Plan zu optimieren.",
        }
      } else {
        return null // Kein Tooltip nötig, wenn es weder Abweichung noch Bewertung gibt.
      }

      // SCHRITT 2: Prüfe IMMER, ob eine Zukunfts-Anpassung existiert und füge sie hinzu.
      if (newWeightEntry) {
        tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
      }

      return tooltipData
    },
    openRatingTooltip(set) {
      this.completedSetsArray.forEach((s) => {
        if (s !== set) {
          this.$set(s, "showMenu", false)
        }
      })
      this.$set(set, "showMenu", !set.showMenu)
    },
    openRetrospectiveFeedback(set, setIndex) {
      this.pendingSet = set
      this.isRetrospective = true
      this.retrospectiveSetIndex = setIndex
      this.triggerFeedbackFlow()
    },
    // In runWorkoutExerciseList.vue -> methods
    editCurrentNote() {
      if (!this.currentSensoProSet) return
      this.editableNote = this.currentSensoProSet.find((s) => s.text === "note")?.value || ""
      this.editingNote = true
    },
    cancelEditNote() {
      this.editingNote = false
      this.editableNote = ""
    },
    saveCurrentNote() {
      const finalSet = [{ text: "note", value: this.editableNote }]
      // Wir nutzen die bestehende Action, um den Satz an Position 0 zu ersetzen
      this.replaceCompletedSet({
        idx: this.selectedExerciseIndex,
        setPos: 0,
        newSet: finalSet,
      })
      this.editingNote = false
    },
    getPlannedWeightForSet(setIndex) {
      if (!this.getSelectedExercise || setIndex < 0) return null

      const completedSet = this.completedSetsArray[setIndex]
      if (!completedSet) return null

      // --- NEUE Logik für Supersätze ---
      if (this.getSelectedExercise.isSuperset) {
        const subExercise = this.getSubExerciseForSet(completedSet)
        if (!subExercise || !subExercise.sets) return null

        // Zähle, der wievielte Satz es für DIESE Sub-Übung ist.
        const subSetIndex = this.completedSetsArray
          .slice(0, setIndex) // Nimm alle Sätze VOR dem aktuellen
          .filter((s) => s.subExerciseId === subExercise._id).length

        const plan = subExercise.sets[subSetIndex]
        return plan?.find((s) => s.text === "Gewicht")?.value || null
      }

      // --- Bestehende Logik für Einzelübungen, leicht verbessert ---
      const isThisWarmup = this.hasWarmup && completedSet.isWarmup

      if (isThisWarmup) {
        return this.getSelectedExercise.warmup?.find((s) => s.text === "Gewicht")?.value || null
      }

      // Berechne den korrekten Index für die regulären Sätze
      const regularSetsDoneBeforeThis = this.completedSetsArray.slice(0, setIndex).filter((s) => !s.isWarmup).length

      const plan = this.getSelectedExercise.sets[regularSetsDoneBeforeThis]
      return plan?.find((s) => s.text === "Gewicht")?.value || null
    },
    getWeightDeviation(set, setIndex) {
      if (!Array.isArray(set) || set.some((s) => s.skipped)) return { deviated: false }
      const plannedWeight = this.getPlannedWeightForSet(setIndex)
      const actualWeight = set.find((s) => s.text === "Gewicht")?.value

      if (plannedWeight === null || actualWeight === undefined || plannedWeight == actualWeight) {
        return { deviated: false }
      }

      return {
        deviated: true,
        planned: plannedWeight,
        actual: actualWeight,
      }
    },
    hasRating(set) {
      if (!Array.isArray(set)) return false
      return set.some((s) => s.text === "Difficulty")
    },
    getFeedbackIcon(set, idx) {
      const deviation = this.getWeightDeviation(set, idx)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        return { isEmoji: true, icon: isIncrease ? "🚀" : "🛠️" }
      }

      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      if (difficultyEntry) {
        switch (difficultyEntry.value) {
          case "hard":
            return { isEmoji: true, icon: "🥵" }
          case "medium":
            return { isEmoji: true, icon: "🙂" }
          case "easy":
            return { isEmoji: true, icon: "😎" }
        }
      }

      // Fallback für einen Satz, der heute gemacht wurde, aber noch kein Feedback hat
      if (this.isTodaySelected) {
        return { isEmoji: true, icon: "💬" }
      }

      return { isEmoji: false, icon: "mdi-minus", color: "grey" }
    },
    // in methods: { ... }

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      // Wenn es keinerlei Feedback gibt, wird kein Tooltip angezeigt.
      if (!deviation.deviated && !difficultyEntry && !newWeightEntry) {
        if (this.isTodaySelected) {
          return {
            type: "rating",
            title: "Feedback fehlt",
            icon: "💬",
            color: "grey",
            details: [{ label: "Bewerte diesen Satz, um deinen Plan zu optimieren." }],
          }
        }
        return null
      }

      // Wir bauen die Informationen für den Tooltip schrittweise auf.
      let title = "Feedback & Plan"
      let icon = "💡"
      let color = "primary"
      let details = []

      // Teil 1: Leistung (Was war geplant vs. was wurde gemacht?)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        icon = isIncrease ? "🚀" : "🛠️"
        title = isIncrease ? "Starke Leistung!" : "Gewichts-Anpassung"
        color = isIncrease ? "primary" : "orange"
        details.push({ label: "Geplant", value: `${deviation.planned} kg` })
        details.push({ label: "Ausgeführt", value: `${deviation.actual} kg`, color: isIncrease ? "primary--text" : "orange--text" })
      }

      // Teil 2: Bewertung (Wie hat es sich angefühlt?)
      if (difficultyEntry) {
        const difficulties = {
          hard: { text: "Zu Schwer", icon: "🥵", color: "#EF5350" },
          medium: { text: "Perfekt", icon: "🙂", color: "#FFCA28" },
          easy: { text: "Zu Leicht", icon: "😎", color: "#66BB6A" },
        }
        const rating = difficulties[difficultyEntry.value]
        if (rating) {
          details.push({ label: "Deine Bewertung", value: rating.text, color: `${rating.color}--text` })
          if (!deviation.deviated) {
            // Wenn die Leistung wie geplant war, bestimmt die Bewertung den Titel.
            title = `Bewertung: ${rating.text}`
            icon = rating.icon
            color = rating.color
          }
        }
      }

      // Teil 3: Entscheidung (Was ist der Plan für die Zukunft?)
      if (newWeightEntry) {
        // Wenn eine Zukunfts-Entscheidung getroffen wurde, ist das die wichtigste Info.
        title = "Feedback & Plan-Update"
        icon = "🎯"
        color = "primary"
        details.push({ label: "Neues Startgewicht", value: `${newWeightEntry.value} kg`, color: "primary--text" })
      }

      return { title, icon, color, details }
    },
    // 5. NEUE Methode, die beim Klick auf das Feedback-Icon aufgerufen wird
    openFeedbackMenu(set, idx) {
      this.tooltipData = this.getTooltipText(set, idx)
    },
    updateSetRating(setIndex, difficulty) {
      let setToUpdate = [...this.completedSetsArray[setIndex]]
      const setInArray = this.completedSetsArray[setIndex]
      if (setInArray) this.$set(setInArray, "showMenu", false)
      setToUpdate = setToUpdate.filter((item) => item.text !== "Difficulty")
      setToUpdate.push({ text: "Difficulty", value: difficulty })
      this.updateCompletedSet({
        exerciseIndex: this.selectedExerciseIndex,
        setIndex: setIndex,
        updatedSet: setToUpdate,
      })
    },
    realSetNumber(idx) {
      if (!this.hasWarmup) return idx + 1
      let count = 0
      for (let i = 1; i <= idx; i++) {
        if (!this.completedSetsArray[i] || !this.completedSetsArray[i].skipped) {
          count++
        }
      }
      return count
    },
    formatTimeForDisplay(totalSeconds) {
      if (totalSeconds < 0) totalSeconds = 0
      return `${totalSeconds}s`
    },
    formatCountdownDisplay(currentSeconds, isOvertimeValue = false) {
      if (currentSeconds < 0) currentSeconds = 0
      const prefix = isOvertimeValue && currentSeconds > 0 ? "+" : ""
      return `${prefix}${currentSeconds}`
    },
    getSubExerciseForSet(set) {
      if (!this.getSelectedExercise?.isSuperset || !set.subExerciseId) return null
      return this.getSelectedExercise.exercises.find((ex) => ex._id === set.subExerciseId)
    },
    getSetNumber(set, overallIndex) {
      if (!this.getSelectedExercise.isSuperset) {
        // Alte Logik für Einzelübungen
        if (this.hasWarmup) return this.realSetNumber(overallIndex)
        return overallIndex + 1
      }
      // Neue Logik für Supersätze: Zähle nur die Sätze für DIESE Sub-Übung
      const setsForThisSubExercise = this.completedSetsArray.filter((s) => s.subExerciseId === set.subExerciseId)
      return setsForThisSubExercise.findIndex((s) => s.id === set.id || s === set) + 1
    },
    showImage(exercise) {
      if (!exercise) return
      this.detailDialogExercise = exercise
      this.exerciseDetailDialog = true
    },
  },
  watch: {
    getSelectedExercise(newVal, oldVal) {
      if (newVal && (!oldVal || newVal._id !== oldVal._id)) {
        this.slideIndex = 0
        this.isIntensityAlertExpanded = false
        this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(newVal._id)
      }
      if (!newVal) {
        this.isIntensityAlertExpanded = false
        this.clearRestTimer()
      }
    },
  },
  mounted() {
    window.addEventListener("visibilitychange", this.handleVisibilityChange)
  },

  beforeDestroy() {
    this.clearRestTimerInterval()
    window.removeEventListener("visibilitychange", this.handleVisibilityChange)
  },
}
</script>
<style scoped>
.skipped-warmup-row {
  cursor: pointer;
  background-color: rgba(255, 165, 0, 0.1);
}
.skipped-warmup-row:hover {
  background-color: rgba(255, 165, 0, 0.2) !important;
}
.intensity-alert {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
/* NEUE KLASSEN FÜR GLAS-EFFEKT & SUPERSETS */
.equipment-card,
.finished-sets-card {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
.glass-card-dialog {
  background: rgba(40, 40, 40, 0.7);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.superset-image-container {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
.superset-avatar-wrapper {
  transition: transform 0.3s ease-in-out;
  text-align: center;
}
.active-superset-exercise .v-avatar {
  transform: translateY(-10px);
  border: 4px solid orange;
  box-shadow: 0 0 10px orange !important;
}
.clickable-avatar {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.clickable-avatar:hover {
  transform: scale(1.2);
}
.tooltip-style {
  background-color: rgba(0, 0, 0, 0.8) !important;
  color: white !important;
}
/* ENDE NEUE KLASSEN */

/* ALTE, WIEDERHERGESTELLTE STILE */
.superset-label {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  color: orange;
  opacity: 0.8;
}
.feedback-tooltip-card {
  background-color: #2e2e2e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px !important;
  padding: 12px;
  color: white !important;
}
.feedback-tooltip-card .caption {
  color: rgba(255, 255, 255, 0.7) !important;
}
.caption-container {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
}
.caption-text {
  color: #fff;
  font-size: 0.9rem;
}
.rating-icon:hover,
.pr-activator .v-icon:hover {
  transform: scale(1.2);
  transition: transform 0.2s ease-in-out;
}
.exercise-list {
  background: #000;
}
.section-header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 75%;
  max-width: 400px;
  padding: 12px 16px;
  border-radius: 16px; /* Abgerundete Ecken */
  border: 1px solid rgba(255, 255, 255, 0.15); /* Feiner Rand für den Glas-Effekt */
  backdrop-filter: blur(12px); /* Der Unschärfe-Effekt */
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1); /* Tiefe und Innenrand */
  transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
}

.section-header-content .section-title {
  font-size: 1.1rem; /* Grössere Schrift */
  font-weight: 700; /* Fetter */
  text-transform: uppercase; /* Grossbuchstaben */
  letter-spacing: 0.05em; /* Etwas mehr Abstand */
  color: #f0f0f0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5); /* Text-Schatten für Lesbarkeit */
}

/* Spezifische Farben und Gradienten */
.section-header-content.warmup {
  background: linear-gradient(135deg, rgba(255, 140, 0, 0.5), rgba(255, 80, 0, 0.6));
}

.section-header-content.training {
  background: linear-gradient(135deg, rgba(60, 60, 60, 0.5), rgba(30, 30, 30, 0.6));
}

.section-header-content.finish {
  background: linear-gradient(135deg, var(--v-primary-base), rgba(40, 95, 120, 0.7)); /* Passend zum Theme */
  cursor: pointer;
}

/* Interaktiver Effekt für den "Abschliessen"-Button */
.section-header-content.finish:hover {
  transform: scale(1.03); /* Leichte Vergrösserung bei Hover */
  box-shadow: 0 8px 25px rgba(var(--v-primary-base), 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2);
}
.section-header {
  margin-bottom: 16px;
  align-items: center;
  position: relative;
}
.section-title {
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  align-content: center;
}
.preset-label {
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
}
.card-inner {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgb(255 255 255 / 50%) !important;
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative; /* Hinzufügen */
  z-index: 1; /* Hinzufügen, um es über die Linie des vorherigen Elements zu heben */
}
.completed-checkmark {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}
.exercise-card--warmup .card-inner,
.superset-card-inner {
  border-color: orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}

/* NEU: Seitlicher Glow für Intensitätstechniken */

.exercise-card-inner--completed {
  box-shadow: 0 1px 6px green !important;
  border: 2px solid green !important;
}
.card-img {
  object-fit: cover;
  margin-right: 16px;
}
.title {
  color: #fff;
  font-size: 1rem !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.intensity-chip {
  font-size: 0.75rem;
  height: auto;
  padding: 2px 6px;
  margin-top: 2px;
  margin-bottom: 2px;
}
.intensity-chip .v-icon {
  font-size: 0.9em;
}
.subtitle-2 {
  color: #ccc;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}
.preset-chip {
  font-size: 1rem;
  margin-right: 6px;
  margin-bottom: 6px;
  padding: 4px 8px;
}
.no-sets {
  color: #bbb;
  font-size: 1rem;
  padding: 8px 0;
}
.plan-settings {
  display: flex;
  flex-wrap: wrap;
  margin-top: 12px;
}
.finished-sets-card {
  margin-bottom: 16px;
}
.completed-table th,
.completed-table td {
  padding: 10px 12px;
  font-size: 0.95rem;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.completed-table tr:last-child td {
  border-bottom: none !important;
}
.completed-table th {
  color: #9e9e9e !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.orange--border {
  border: 2px solid orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}
.orange-dot {
  background-color: #ffa500 !important;
}
.overlay {
  position: fixed;
  inset: 0;
  background: #1e1e1e;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  z-index: 10;
}
.dialog-card.fullscreen {
  width: 100%;
  max-width: 100vw;
  height: 100%;
  overflow-y: auto;
  border-radius: 0;
}
.dialog-content {
  width: 100%;
}
.exercise-card {
  position: relative;
  width: calc(100% - 16px);
  margin-bottom: 16px;
}
/*
.exercise-card:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 100%; 
  height: 16px; 
  width: 2px;
  background: grey;
  z-index: 0;
  transition: background-color 0.3s ease;
}
  */
.exercise-card--warmup:not(:last-child)::after {
  background: orange !important;
}
.exercise-card.exercise-line-completed:not(.exercise-card--warmup):not(:last-child)::after {
  background: green;
}
.exercise-card--warmup.exercise-line-completed:not(:last-child)::after {
  background: green !important;
}
/*.section-header::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 100%; 
  height: 16px; 
  width: 2px;
  z-index: 0;
  transition: background-color 0.3s ease;
} 
*/
.warmup-marker.section-header::after {
  background: orange;
}
.training-marker.section-header::after {
  background: grey;
}
::v-deep .v-list-item--link:before {
  background-color: transparent !important;
}
.table-container-transition-enter-active,
.table-container-transition-leave-active {
  transition: opacity 0.2s ease-out;
}
.table-container-transition-enter,
.table-container-transition-leave-to {
  opacity: 0;
}
.row-enter-active {
  transition: opacity 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.6s ease-out;
}
.row-leave-active {
  transition: opacity 0.35s cubic-bezier(0.75, 0, 0.25, 1), transform 0.35s cubic-bezier(0.75, 0, 0.25, 1),
    background-color 0.35s cubic-bezier(0.75, 0, 0.25, 1);
  position: absolute;
  width: 100%;
  left: 0;
  box-sizing: border-box;
  z-index: 1;
  background-color: rgba(217, 83, 79, 0.65);
  transform-origin: center center;
}
.row-enter {
  opacity: 0;
  transform: translateY(15px) scale(0.97);
  background-color: rgba(40, 167, 69, 0.3);
}
.row-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  background-color: transparent;
}
.row-leave-to {
  opacity: 0;
  transform: scale(0.85);
  background-color: rgba(217, 83, 79, 0.3);
}
.completed-table tbody tr td {
  background-color: transparent !important;
}
.row-enter td,
.row-enter-to td,
.row-leave-active td,
.row-leave-to td {
  background-color: inherit !important;
}
.row-move {
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}
.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.8);
  z-index: 2000;
  backdrop-filter: blur(8px);
}
.feedback-card {
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  transition: max-height 0.4s ease-in-out;
  overflow: hidden;
  max-height: 100vh; /* Verhindert, dass die Karte zu gross wird */
  /* NEU: Animierter Hintergrund-Glow */
  background: radial-gradient(circle at 50% 0%, rgba(var(--v-primary-base-rgb), 0.15), transparent 70%);
  animation: bg-glow-pan 8s infinite alternate;
}
.feedback-section {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.status-title {
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
.countdown-text {
  font-size: 4rem;
  line-height: 1;
  font-weight: 900;
  font-family: "Roboto Condensed", "Roboto", sans-serif;
  letter-spacing: -0.02em;
}
.countdown-text-smaller {
  font-size: 3.5rem !important;
}
.completed-table tbody .clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}
.completed-table tbody .clickable-row:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.clickable-row td:last-child .v-icon {
  cursor: help;
}
.weight-suggestion-btn {
  flex-grow: 1;
  margin: 0 4px;
}
.rest-timer-overlay-fade-enter-active,
.rest-timer-overlay-fade-leave-active {
  transition: opacity 0.25s ease-in-out;
}
.rest-timer-overlay-fade-enter,
.rest-timer-overlay-fade-leave-to {
  opacity: 0;
}
.smiley-label {
  font-size: 0.75rem;
  margin-top: 4px;
  font-weight: 500;
}
.info-list .v-list-item-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  word-wrap: break-word !important;
}
.v-menu__content {
  z-index: 2001 !important;
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
@media (max-width: 380px) {
  .countdown-text {
    font-size: 3.5rem;
  }
  .countdown-text-smaller {
    font-size: 3rem !important;
  }
  .feedback-card {
    padding: 12px !important;
  }
  .text-h5.status-title {
    font-size: 1.3rem !important;
  }
}
.dialog-content::before {
  content: "";
  position: absolute;
  top: -1px; /* Korrigiert, um direkt am Rand zu sein */
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(to bottom, #1e1e1e, transparent);
  z-index: 5;
  pointer-events: none;
}

/* Spotlight-Effekt für die Übungsliste */
.exercise-card {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.is-inactive-exercise-focus {
  opacity: 0.4;
  transform: scale(0.95);
}
.is-active-exercise-focus {
  opacity: 1;
  transform: scale(1.02);
}

/* Verbesserte Typografie für die Vorgaben-Chips */
.preset-chip {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.preset-chip .v-icon {
  margin-right: 6px;
  color: rgba(255, 255, 255, 0.6);
}
.overlay {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.main-card-container {
  width: 100%;
  height: 100%;
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), filter 0.4s ease;
}
.main-card-container.is-dragging-over {
  transform: scale(0.95) !important;
  filter: blur(4px);
}
.swipe-handle {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 15;
  width: 24px; /* Schmaler, dient nur als unsichtbarer Interaktionsbereich */
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  background: transparent; /* Der Handle selbst ist unsichtbar */
}

.swipe-handle.left {
  left: 0;
}
.swipe-handle.right {
  right: 0;
}

/* Der sichtbare, animierte "Griff" */
.swipe-indicator {
  width: 4px;
  height: 40px;
  border-radius: 2px;

  /* Wichtig für den Shimmer-Effekt: Der Gradient ist viel grösser als das Element */
  background-size: 100% 400%;
  background-image: linear-gradient(
    to bottom,
    rgba(var(--v-primary-base-rgb), 0.2),
    rgba(255, 255, 255, 0.9),
    /* Der helle "Licht"-Teil */ rgba(var(--v-primary-base-rgb), 0.2)
  );

  /* Die Animation, die den Gradienten auf und ab bewegt */
  animation: shimmer 3s infinite ease-in-out;

  /* Ein passender Glow-Effekt, der die Primärfarbe des Themes nutzt */
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.3), 0 0 12px rgba(var(--v-primary-base-rgb), 0.4);
}

/* Die Keyframe-Animation für den Shimmer-Effekt */
@keyframes shimmer {
  0% {
    background-position: center 150%; /* Startet weit unten */
  }
  50% {
    background-position: center -50%; /* Bewegt sich weit nach oben */
  }
  100% {
    background-position: center 150%; /* Kehrt zum Startpunkt zurück */
  }
}

/* Um diese CSS-Variable zu nutzen, musst du sicherstellen,
   dass deine Primärfarbe in RGB in der `:root` deines Haupt-CSS definiert ist.
   z.B. in App.vue:
   :root {
     --v-primary-base-rgb: 25, 118, 210;
   }
*/
.drag-avatar {
  position: fixed;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  z-index: 20;
  pointer-events: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: 3px solid white;
  transform-origin: 0 0;
  /* Erweitere die Transition */
  transition: transform 0.2s cubic-bezier(0, 0, 0.2, 1), box-shadow 0.3s ease, border-color 0.3s ease;
  overflow: hidden;
}

/* NEUE Regel für den "on-target" Zustand */
.drag-avatar.on-target {
  border-color: #4caf50;
  box-shadow: 0 0 25px 5px rgba(76, 175, 80, 0.7);
}
.drop-zone-card {
  position: fixed;
  top: 50%;
  width: 200px;
  height: 120px;
  border-radius: 16px;
  z-index: 15;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  opacity: 0;
  transform: translateY(-50%) scale(0.8);
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.drop-zone-card.is-left {
  left: 32px;
}

.drop-zone-card.is-right {
  right: 32px;
}

/* Sichtbar, wenn ein Drag aktiv ist */
.v-application--is-ltr .main-card-container.is-dragging-over + .drop-zone-card,
.v-application--is-rtl .main-card-container.is-dragging-over + .drop-zone-card {
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.drop-zone-image {
  filter: grayscale(80%);
  transition: filter 0.3s ease;
}

.drop-zone-card.is-active .drop-zone-image {
  filter: grayscale(0%);
}

.drop-zone-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px;
}

.drop-zone-icon {
  font-size: 32px !important;
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

.drop-zone-card.is-active .drop-zone-icon {
  color: #4caf50; /* Grün */
  transform: scale(1.2);
}

.drop-zone-title {
  font-size: 0.9rem;
  font-weight: bold;
  color: white;
  margin-top: 8px;
  line-height: 1.2;
}
/* Binden der Progress-Farbe an eine CSS-Variable für den Glow-Effekt */
.v-progress-circular {
  filter: drop-shadow(0 0 8px var(--progress-color, rgba(0, 0, 0, 0.5)));
  transition: filter 0.5s ease-in-out;
}

/* Anpassung der Countdown-Schriftart für einen moderneren Look */
.countdown-text {
  font-size: 4.5rem; /* Etwas größer */
  line-height: 1;
  font-weight: 900; /* Extra Fett */
  /* Eine moderne, komprimierte Schriftart wirkt oft gut */
  font-family: "Roboto Condensed", "Impact", sans-serif;
  letter-spacing: -0.04em; /* Buchstaben enger zusammen */
  transition: color 0.5s ease;
}

/* Animation für die letzten Sekunden */
.pulse-warning {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Animation für den "Fertig!" Text */
.status-title.final-state {
  animation: pulse-fade 0.5s ease-out;
}

@keyframes pulse-fade {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsivität für kleinere Bildschirme */
@media (max-width: 380px) {
  .countdown-text {
    font-size: 3.8rem;
  }
}
.celebration-title {
  /* Erzeugt einen schimmernden Regenbogen-Texteffekt */
  background: linear-gradient(90deg, #ff9a9e, #fad0c4, #fad0c4, #ff9a9e, #fbc2eb, #a18cd1, #fbc2eb, #ff9a9e);
  background-size: 200% auto;
  color: #000;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: celebrate-text-shine 4s linear infinite;
  font-weight: 900 !important;
}

@keyframes celebrate-text-shine {
  to {
    background-position: 200% center;
  }
}

.feedback-card.celebration-bg {
  /* Erzeugt einen pulsierenden, leuchtenden Hintergrund */
  animation: celebrate-bg-pulse 2s infinite ease-in-out;
}

@keyframes celebrate-bg-pulse {
  0% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 165, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 35px rgba(255, 215, 0, 0.7), 0 0 50px rgba(255, 165, 0, 0.5);
  }
  100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 165, 0, 0.3);
  }
}

.glass-card-dialog {
  background: rgba(40, 40, 40, 0.8) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.weight-box {
  padding: 12px 8px;
  border-radius: 12px; /* Etwas runder */
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 4px;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px; /* Mehr Abstand */
}

.new-weight.increase {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(102, 222, 132, 0.5);
  color: #a5d6a7;
  text-shadow: 0 0 8px rgba(102, 222, 132, 0.7);
}

.new-weight.decrease {
  background: rgba(251, 140, 0, 0.2);
  border-color: rgba(251, 140, 0, 0.5);
  color: #ffcc80;
  text-shadow: 0 0 8px rgba(251, 140, 0, 0.7);
}

.new-weight-value {
  opacity: 0;
  animation: new-weight-pop-in 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

@keyframes new-weight-pop-in {
  from {
    transform: scale(0.5) translateY(10px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* NEU: Styles für den vertikalen Flow-Indikator */
.connector-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px; /* Höhe des Bereichs */
  margin: -8px 0;
}
.connector-wrapper::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.1));
}

.operation-indicator {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.1rem;
  z-index: 1;
  border: 2px solid;
  animation: pulse-glow 2s infinite ease-in-out;
}
.operation-indicator .v-icon {
  font-size: 1.2rem;
  margin-bottom: -2px;
}
.operation-indicator.increase {
  background: radial-gradient(circle, rgba(76, 175, 80, 0.3) 0%, rgba(30, 30, 30, 0) 70%);
  border-color: rgba(102, 222, 132, 0.8);
  color: #a5d6a7;
}
.operation-indicator.decrease {
  background: radial-gradient(circle, rgba(251, 140, 0, 0.3) 0%, rgba(30, 30, 30, 0) 70%);
  border-color: rgba(251, 140, 0, 0.8);
  color: #ffcc80;
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 15px -5px currentColor;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 25px 0px currentColor;
    transform: scale(1.05);
  }
}
.completed-table th {
  white-space: nowrap;
}

/* Style für die neuen Feedback-Emojis in der Tabelle */
.feedback-emoji {
  font-size: 1.5rem; /* Emojis größer und auffälliger machen */
  line-height: 1;
  vertical-align: middle;
}

.feedback-activator {
  cursor: pointer;
  display: inline-block;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s ease-in-out;
}

.feedback-activator:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Völlig neues Design für den Tooltip */
.feedback-tooltip-card-v2 {
  min-width: 300px;
  border-radius: 16px !important;
  /* Glass-Effekt wird von deiner bestehenden Klasse übernommen */
}
.feedback-tooltip-card-v2 .v-card__title .title {
  font-size: 1.1rem !important;
  font-weight: bold;
}
.feedback-tooltip-card-v2 .v-card__text {
  font-size: 0.9rem;
  line-height: 1.5;
}

.rating-options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  width: 100%;
}

.rating-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.rating-option:hover {
  background: rgba(255, 255, 255, 0.1);
}

.rating-option.rating-hard:hover {
  border-color: #ef5350;
}
.rating-option.rating-medium:hover {
  border-color: #ffca28;
}
.rating-option.rating-easy:hover {
  border-color: #66bb6a;
}

.rating-emoji {
  font-size: 2.5rem;
  margin-right: 16px;
  line-height: 1;
}

.rating-text {
  text-align: left;
}

.rating-label {
  font-weight: bold;
  font-size: 1rem;
  color: white;
}

.rating-desc {
  font-size: 0.8rem;
  color: #bdbdbd;
}

.rating-info-emoji {
  font-size: 2rem;
  opacity: 0.9;
}

/* KORREKTUR FÜR INFO-ANSICHT (Stelle sicher, dass dies in deinem CSS ist) */
.info-list .v-list-item-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

@keyframes bg-glow-pan {
  from {
    background-position: 50% -50px;
  }
  to {
    background-position: 50% 100px;
  }
}

/* NEUE Struktur für Scrollbarkeit */
.feedback-card__header {
  flex-shrink: 0;
}
.feedback-card__content {
  flex-grow: 1;
  overflow-y: auto; /* HIER PASSIERT DIE MAGIE! */
  padding: 0 4px; /* Kleiner seitlicher Abstand für die Scrollleiste */
}
.feedback-card__footer {
  flex-shrink: 0;
}

/* NEU: Visuelles Feedback für ausgewählte Option */
.rating-option.is-selected {
  border-width: 2px;
}
.rating-option.rating-hard.is-selected {
  border-color: #ef5350;
  box-shadow: 0 0 15px #ef535066;
}
.rating-option.rating-medium.is-selected {
  border-color: #ffca28;
  box-shadow: 0 0 15px #ffca2866;
}
.rating-option.rating-easy.is-selected {
  border-color: #66bb6a;
  box-shadow: 0 0 15px #66bb6a66;
}

/* KORREKTUR FÜR INFO-ANSICHT (Robusterer CSS-Selektor) */
.info-list .info-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  overflow-wrap: break-word !important;
  height: auto !important; /* Verhindert, dass Vuetify die Höhe beschränkt */
  display: block !important;
}

/* NEU: Schönere Trennlinie */
.gradient-divider {
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
  height: 1px;
  border: none;
}

/* Fade-In Animation für das Overlay verbessern */
.rest-timer-overlay-fade-enter-active,
.rest-timer-overlay-fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.rest-timer-overlay-fade-enter-active .feedback-card,
.rest-timer-overlay-fade-leave-active .feedback-card {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.rest-timer-overlay-fade-enter,
.rest-timer-overlay-fade-leave-to {
  opacity: 0;
}
.rest-timer-overlay-fade-enter .feedback-card,
.rest-timer-overlay-fade-leave-to .feedback-card {
  transform: scale(0.95) translateY(20px);
}
.exercise-card-inner--completed-hyrox {
  box-shadow: 0 1px 6px #ffed00 !important;
  border: 2px solid #ffed00 !important;
}

.incomplete-dialog-card {
  border-radius: 24px !important;
  background: #1e1e1e !important;
  border: 1px solid rgba(251, 140, 0, 0.3);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(251, 140, 0, 0.2);
}

.incomplete-dialog-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(251, 140, 0, 0.15), transparent 70%);
  pointer-events: none;
}

.alert-icon {
  animation: alert-icon-pop 0.8s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  opacity: 0;
}

.incomplete-list .v-list-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

@keyframes alert-icon-pop {
  0% {
    transform: scale(0.6) rotate(-15deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.1) rotate(10deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}
</style>
